import { MobXProviderContext } from "mobx-react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { MenuItem } from "primereact/menuitem";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { TieredMenu } from "primereact/tieredmenu";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";

import { Toast } from 'primereact/toast';
import { Tooltip } from "primereact/tooltip";
import { ReactNode, cloneElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, getToastMessageForSubscriptionLimitReached, JOB_STATUS, JOB_TYPE, RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES, RESIDENT_CARE_PLAN_TYPE } from "../../../constants/constants";
import { BillingConstants } from "../../../constants/billing";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { openUrlInNewTab, sanitizeRichHtml } from "../../../utils/securityUtils";
import { calculateDeepAge, capitalizeFirstLetter, getStatusNameForDisplay, withinLast11Months } from "../../../utils/stringUtils";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { BRAND_KEY_ONE_HUB_FOR_CARE } from "../../../constants/brandConstants";
import EditResidentModal from "../../home/components/EditResidentModal";
import ActivitiesOfDailyLivingSection from "../careplan/activitiesOfDailyLivingSection";
import BodyChartSection from "../careplan/bodyChartSection";
import CommunicationSection from "../careplan/communicationSection";
import HealthIndicatorSection from "../careplan/healthIndicatorSection";
import InstrumentalActivitiesSection from "../careplan/instrumentalActivitiesSection";
import LeftAloneSection from "../careplan/leftAloneSection";
import MedicationManagementSection from "../careplan/medicationManagementSection";
import PsychSocialSection from "../careplan/psychSocialSection";
import ResponsiblePartiesSection from "../careplan/responsiblePartiesSection";
import SummarySection from "../careplan/summarySection";
import TreatmentProgramSection from "../careplan/treatmentProgramSection";
import UniversalPrecautionSection from "../careplan/universalPrecautionSection";
import {
  ESF_MEDICAL_ALLERGIES_CARE_PLAN_TYPE,
  EsfMedicalAllergiesSection,
  EsfResponsiblePartiesSection,
  EsfSummarySection,
} from "../esf/EsfCarePlanSections";
import {
  DEFAULT_ACTIVITY_SECTION, DEFAULT_COMMUNICATION_SECTION, DEFAULT_HEALTH_INDICATOR_SECTION,
  DEFAULT_BODY_CHART_SECTION,
  DEFAULT_INSTRUMENTAL_ACTIVITY_SECTION, DEFAULT_LEFT_ALONE_SECTION, DEFAULT_MEDICATION_SECTION,
  DEFAULT_PSYCH_SOCIAL_SECTION, DEFAULT_SUMMARY_SECTION,
  DEFAULT_TREATMENT_SECTION,
  DEFAULT_UNIVERSAL_PRECAUTIONS_SECTION
} from "../../../constants/carePlanConstants";
import { BODY_CHART_CARE_PLAN_TYPE } from "../../../constants/carePlanBodyChart";
import { FileUpload } from "primereact/fileupload";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { Binder, BinderDocument, BinderFolder } from "../../../models/binder";
import {
  appConfirmDialogClasses,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appSectionCardStyle,
  renderConfirmMessage,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { dashboardPageContainerStyle, renderDashboardHeader } from "../../../features/dashboard/pages/dashboardStyles";
import {
  ensureResidentBinderBundle,
  getBinderFolderByCode,
  getFolderDocuments,
  getLatestBinderDocumentVersion,
  getPrimaryDocument,
  RESIDENT_BINDER_FOLDER_CODES,
  upsertBinderDocumentWithVersion,
} from "../../../utils/binderFeatureUtils";
import { useTour } from "../../../shared/tour/TourProvider";

const ASSESSMENT_UPLOAD_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ASSESSMENT_UPLOAD_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx"];
const FACILITY_DOCUMENT_SOURCE_UPLOAD_ACCEPT = ".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/tiff";
const FACILITY_DOCUMENT_SOURCE_UPLOAD_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "txt", "png", "jpg", "jpeg", "tif", "tiff"];
const BEHAVIOR_IMPORT_UPLOAD_ACCEPT = ".pdf,.doc,.docx,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/tiff";
const BEHAVIOR_IMPORT_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "png", "jpg", "jpeg", "tif", "tiff"];

const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const result = String(fileReader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    fileReader.onerror = () => reject(new Error("Unable to read the selected file."));
    fileReader.readAsDataURL(file);
  });

const NCP_STEPS: { label: string; shortLabel: string; icon: string }[] = [
  { label: "Summary", shortLabel: "Summary", icon: "pi pi-file-edit" },
  { label: "Responsible parties", shortLabel: "Resp. Parties", icon: "pi pi-users" },
  { label: "Communication - speech, hearing & vision", shortLabel: "Communication", icon: "pi pi-comments" },
  { label: "Medication management", shortLabel: "Medications", icon: "pi pi-heart" },
  { label: "Health indicators", shortLabel: "Health", icon: "pi pi-chart-line" },
  { label: "Body chart", shortLabel: "Body Chart", icon: "pi pi-map" },
  { label: "Treatment programs & therapies", shortLabel: "Treatment", icon: "pi pi-list-check" },
  { label: "Psychosocial & cognitive", shortLabel: "Psych/Cog", icon: "pi pi-brain" },
  { label: "Left alone status", shortLabel: "Left Alone", icon: "pi pi-user" },
  { label: "Universal precautions", shortLabel: "Precautions", icon: "pi pi-shield" },
  { label: "Activities of daily living", shortLabel: "Daily Living", icon: "pi pi-home" },
  { label: "Instrumental activities of daily living", shortLabel: "Instrumental", icon: "pi pi-wrench" },
];

const residentNcpHelpHtmlStyle = `
  .resident-ncp-help-html {
    color: #475569;
    line-height: 1.7;
  }
  .resident-ncp-help-html p,
  .resident-ncp-help-html div {
    margin: 0 0 0.85rem 0;
  }
  .resident-ncp-help-html ul,
  .resident-ncp-help-html ol {
    margin: 0.35rem 0 1rem 1.25rem;
    padding-left: 0.9rem;
  }
  .resident-ncp-help-html li {
    margin-bottom: 0.45rem;
  }
  .resident-ncp-help-html strong,
  .resident-ncp-help-html b {
    color: #0f172a;
  }
  .resident-ncp-help-html br {
    display: block;
    content: "";
    margin-top: 0.45rem;
  }
`;

const renderAccordionHeader = (index: number, label: string, icon: string, activeStep: number | null) => (
  <span style={{ display: "flex", alignItems: "center", gap: "0.625rem", fontWeight: 600, fontSize: "0.9rem" }}>
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 22, height: 22, borderRadius: "50%",
      background: index < (activeStep ?? -1) ? "#f97316" : index === activeStep ? "#1d4ed8" : "#94a3b8",
      color: "#fff", fontSize: "0.68rem", fontWeight: 700, flexShrink: 0,
    }}>
      {index < (activeStep ?? -1)
        ? <i className="pi pi-check" style={{ fontSize: "0.58rem" }} />
        : String(index + 1).padStart(2, "0")}
    </span>
    <i className={icon} style={{ color: index === activeStep ? "#1d4ed8" : "#64748b", marginLeft: 2 }} />
    {label}
  </span>
);

const isSubscriptionAccessError = (error: any) => {
  const code = String(error?.code || error?.cause?.message || error?.details?.code || "").trim().toUpperCase();
  return code === "SUBSCRIPTION_REQUIRED" || code === "SUBSCRIPTION_LIMIT_REACHED";
};

const isFacilityDocumentLongFormField = (field: any) => {
  const fieldKey = String(field?.fieldKey || "").trim().toLowerCase();
  const label = String(field?.label || "").trim().toLowerCase();
  const value = String(field?.value || "");
  if (value.includes("\n") || value.length > 160) {
    return true;
  }
  return [
    "details",
    "description",
    "summary",
    "support",
    "barrier",
    "activities",
    "transportation",
    "medical_follow_up",
    "significant_behaviors",
    "special_safety_needs",
    "mental_health_services",
    "cognitive_needs",
    "acknowledgement_text",
    "notes",
    "preferences",
    "plan",
    "discussion",
    "history",
    "status",
    "diagnoses",
  ].some((token) => fieldKey.includes(token) || label.includes(token));
};

const FACILITY_DOCUMENT_LEVEL_OPTIONS = [
  { label: "Independent", value: "independent" },
  { label: "Situational support", value: "situational" },
  { label: "Fluctuating support", value: "fluctuating" },
  { label: "Full assistance", value: "full_assistance" },
];

const parseFacilityDocumentDraftDate = (value: string): Date | null => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) {
    return null;
  }

  const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]), 12, 0, 0, 0);
  }

  const slashMatch = normalizedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    const rawYear = Number(slashMatch[3]);
    const year = rawYear < 100 ? 2000 + rawYear : rawYear;
    return new Date(year, Number(slashMatch[1]) - 1, Number(slashMatch[2]), 12, 0, 0, 0);
  }

  return null;
};

const formatFacilityDocumentDraftDate = (value: Date | null): string => {
  if (!value || Number.isNaN(value.getTime())) {
    return "";
  }
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const inferFacilityDocumentDraftFieldPresentation = (field: ResidentFacilityDocumentDraftField) => {
  const explicitInputType = String(field?.inputType || "").trim().toLowerCase();
  if (explicitInputType === "date" || explicitInputType === "textarea" || explicitInputType === "checkbox" || explicitInputType === "radio" || explicitInputType === "dropdown") {
    return {
      inputType: explicitInputType as "date" | "textarea" | "checkbox" | "radio" | "dropdown",
      options: Array.isArray(field?.options) ? field.options : [],
    };
  }

  const fieldKey = String(field?.fieldKey || "").trim().toLowerCase();
  const normalizedValue = String(field?.value || "").trim().toLowerCase();
  const label = String(field?.label || "").trim();

  if (fieldKey === "identified_level") {
    return {
      inputType: "radio" as const,
      options: FACILITY_DOCUMENT_LEVEL_OPTIONS,
    };
  }

  const isSingleDateField =
    fieldKey === "date" ||
    fieldKey === "date_of_birth" ||
    fieldKey.endsWith("_date") ||
    /^date(\b| of\b)/i.test(label);
  if (isSingleDateField && fieldKey !== "dates_of_visits") {
    return {
      inputType: "date" as const,
      options: [],
    };
  }

  if ((normalizedValue === "yes" || normalizedValue === "no") && !isFacilityDocumentLongFormField(field)) {
    return {
      inputType: "radio" as const,
      options: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
    };
  }

  if ((normalizedValue === "true" || normalizedValue === "false") && !isFacilityDocumentLongFormField(field)) {
    return {
      inputType: "checkbox" as const,
      options: [],
    };
  }

  if (Array.isArray(field?.options) && field.options.length > 0) {
    return {
      inputType: "dropdown" as const,
      options: field.options,
    };
  }

  if (isFacilityDocumentLongFormField(field)) {
    return {
      inputType: "textarea" as const,
      options: [],
    };
  }

  return {
    inputType: "text" as const,
    options: [],
  };
};

const CHALLENGING_BEHAVIORS_SECTION_KEY = "challenging_behaviors";

const getChallengingBehaviorRowIndexes = (fields: ResidentFacilityDocumentDraftField[]) =>
  Array.from(new Set(
    (Array.isArray(fields) ? fields : [])
      .map((field) => {
        const match = String(field?.fieldKey || "").trim().match(/^(?:behavior_selection|behavior_details|intervention_selection|intervention_details)_(\d+)$/);
        return match ? Number(match[1]) : 0;
      })
      .filter((value) => value > 0),
  )).sort((left, right) => left - right);

const extractChallengingBehaviorRows = (fields: ResidentFacilityDocumentDraftField[]): ResidentChallengingBehaviorRow[] => {
  const fieldMap = new Map(
    (Array.isArray(fields) ? fields : []).map((field) => [String(field?.fieldKey || "").trim(), String(field?.value || "")]),
  );
  const rowIndexes = getChallengingBehaviorRowIndexes(fields);
  const rows = rowIndexes.map((rowId) => ({
    rowId,
    behaviorKey: String(fieldMap.get(`behavior_selection_${rowId}`) || ""),
    behaviorDetails: String(fieldMap.get(`behavior_details_${rowId}`) || ""),
    interventionKeys: String(fieldMap.get(`intervention_selection_${rowId}`) || "")
      .split(",")
      .map((value) => String(value || "").trim())
      .filter(Boolean),
  }));

  return rows.length ? rows : [{
    rowId: 1,
    behaviorKey: "",
    behaviorDetails: "",
    interventionKeys: [],
  }];
};

const buildChallengingBehaviorFields = (
  rows: ResidentChallengingBehaviorRow[],
  existingFields: ResidentFacilityDocumentDraftField[],
): ResidentFacilityDocumentDraftField[] => {
  const behaviorTemplate = (existingFields || []).find((field) => /^behavior_selection_\d+$/.test(String(field?.fieldKey || "").trim()));
  const detailsTemplate = (existingFields || []).find((field) => /^behavior_details_\d+$/.test(String(field?.fieldKey || "").trim()));
  const interventionTemplate = (existingFields || []).find((field) => /^intervention_selection_\d+$/.test(String(field?.fieldKey || "").trim()));
  const progressionTemplate = (existingFields || []).find((field) => String(field?.fieldKey || "").trim() === "behavior_progression_response");
  const behaviorOptions = behaviorTemplate?.options ?? [];
  const interventionOptions = interventionTemplate?.options ?? [];

  const nextRows = rows.length ? rows : [{
    rowId: 1,
    behaviorKey: "",
    behaviorDetails: "",
    interventionKeys: [],
  }];

  return [
    ...nextRows.flatMap((row, index) => {
      const rowNumber = index + 1;
      return [
        {
          fieldKey: `behavior_selection_${rowNumber}`,
          label: behaviorTemplate?.label || "Behavior Name",
          value: row.behaviorKey,
          inputType: "dropdown" as const,
          options: behaviorOptions,
          placeholder: behaviorTemplate?.placeholder || "Select behavior",
        },
        {
          fieldKey: `behavior_details_${rowNumber}`,
          label: detailsTemplate?.label || "Details",
          value: row.behaviorDetails,
          inputType: "textarea" as const,
        },
        {
          fieldKey: `intervention_selection_${rowNumber}`,
          label: interventionTemplate?.label || "Interventions",
          value: row.interventionKeys.join(","),
          inputType: "dropdown" as const,
          options: interventionOptions,
          placeholder: interventionTemplate?.placeholder || "Select intervention",
        },
      ];
    }),
    {
      fieldKey: "behavior_progression_response",
      label: progressionTemplate?.label || "Behavior Progression / Linkage",
      value: progressionTemplate?.value || "",
      inputType: "textarea" as const,
      placeholder: progressionTemplate?.placeholder,
    },
  ];
};

const normalizeBehaviorInterventionImportSummary = (summary: any) => {
  if (!summary) {
    return summary;
  }

  const reviewMatches = Array.isArray(summary?.reviewMatches)
    ? summary.reviewMatches
    : (Array.isArray(summary?.appliedMatches) ? summary.appliedMatches : []).map((match: any, index: number) => ({
      ...match,
      matchId: String(match?.matchId || `${match?.behaviorLabel || "behavior"}-${index + 1}`),
      selected: match?.selected !== false,
    }));
  const availableBehaviorOptions = Array.isArray(summary?.availableBehaviorOptions) && summary.availableBehaviorOptions.length
    ? summary.availableBehaviorOptions
    : Array.from(
      new Set(
        reviewMatches
          .map((match: any) => String(match?.behaviorLabel || "").trim())
          .filter(Boolean),
      ),
    ).map((behaviorLabel) => ({ behaviorLabel }));

  return {
    ...summary,
    reviewMatches,
    availableBehaviorOptions,
  };
};

const getBehaviorInterventionReviewMatches = (summary: any) =>
  Array.isArray(summary?.reviewMatches) ? summary.reviewMatches : [];

const getSelectedBehaviorInterventionReviewMatches = (summary: any) =>
  getBehaviorInterventionReviewMatches(summary).filter((match: any) =>
    match?.selected !== false
    && String(match?.behaviorLabel || "").trim()
    && (String(match?.behaviorDetails || "").trim() || String(match?.interventionDetails || "").trim()),
  );

type ResidentCarePlanDocumentConfig = {
  documentType: string;
  title: string;
  description: string;
  requiresCarePlanDraft: boolean;
  binderFolderCode?: string;
  draftSchemaKey?: string | null;
};

type ResidentFacilityDocumentDraftField = {
  fieldKey: string;
  label: string;
  value: string;
  inputType?: "text" | "textarea" | "date" | "checkbox" | "radio" | "dropdown";
  options?: Array<{
    label: string;
    value: string;
  }>;
  placeholder?: string;
};

type ResidentFacilityDocumentDraftSection = {
  sectionKey: string;
  title: string;
  description?: string;
  fields: ResidentFacilityDocumentDraftField[];
};

type ResidentFacilityDocumentDraftBundle = {
  documentType: string;
  title: string;
  description: string;
  sections: ResidentFacilityDocumentDraftSection[];
};

type ResidentChallengingBehaviorRow = {
  rowId: number;
  behaviorKey: string;
  behaviorDetails: string;
  interventionKeys: string[];
};

type ResidentCarePlanDocumentBundle = {
  items: any[];
  facilityDocumentConfig?: {
    facilityType: string;
    facilityTypeLabel: string;
    appliedProfileKey: string;
    documents: ResidentCarePlanDocumentConfig[];
  } | null;
};

type CarePlanWorkflowActionItem = {
  documentConfig: ResidentCarePlanDocumentConfig | null;
  icon: string;
  label: string;
  buttonLabel: string;
  severity: "warning" | "secondary";
  disabled: boolean;
  loading: boolean;
  canEditDraft: boolean;
  action: () => void;
  tooltip?: string;
  customControls?: ReactNode;
};

type FacilityDocumentPostAction = "download" | "binder";
type FacilityDocumentActionTarget = {
  documentType: string;
  label: string;
};

const EsfDocumentMenuButton = ({
  menuItem,
  disabled,
  loading,
}: {
  menuItem?: MenuItem;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const menuRef = useRef<TieredMenu>(null);
  const menuModel = ((menuItem?.items || []).flat() as MenuItem[]);

  return (
    <div className="flex align-items-center justify-content-end">
      <TieredMenu model={menuModel} popup ref={menuRef} breakpoint="960px" />
      <Button
        label="Actions"
        severity="secondary"
        rounded
        outlined
        size="small"
        icon="pi pi-chevron-down"
        iconPos="right"
        disabled={disabled}
        loading={loading}
        onClick={(event) => menuRef.current?.toggle(event)}
      />
    </div>
  );
};

const ResidentNcpPage = () => {
  const accordionRef = useRef<HTMLDivElement>(null);
  const autoSavedCompletedNcpJobIdsRef = useRef<Set<string>>(new Set());

  const { homeId, residentId } = useParams();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const location = useLocation();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const { completeStep, isStepActive } = useTour();
  const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/careplan/");
  const isEsfCarePlanMode = location.pathname.includes("/esf/careplan/");
  const requiresProviderSubscriptionMessageOnly = isSharedResidentMode;
  const residentBinderPath = isSharedResidentMode
    ? RouterAddress.getSharedResidentBinderPagePath(homeId || "", residentId || "")
    : RouterAddress.getResidentBinderPagePath(homeId!, residentId!);
  const residentNcpPath = isSharedResidentMode
    ? RouterAddress.getSharedResidentCarePlanPagePathForFacility(homeId || "", residentId || "", isEsfCarePlanMode ? "esf" : "")
    : isEsfCarePlanMode
      ? RouterAddress.getResidentEsfCarePlanPagePath(homeId || "", residentId || "")
    : RouterAddress.getResidentNCPPagePath(homeId!, residentId!);

  const [editProfileModalOpen, setEditProfileModalOpen] = useState<boolean>(false);
  const [updateFlag, setUpdateFlag] = useState<boolean>(false);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const [residentProfileData, setResidentProfileData] = useState<any>(null);

  const [loadingCarePlan, setLoadingCarePlan] = useState<boolean>(false);
  const [fieldAutoSaveIndicator, setFieldAutoSaveIndicator] = useState<{
    visible: boolean;
    top: number;
    left: number;
    message: string;
    state: "saving" | "saved";
  }>({
    visible: false,
    top: 0,
    left: 0,
    message: "",
    state: "saved",
  });
  const [doesCarePlanExist, setDoesCarePlanExist] = useState<boolean>(false);
  const [carePlanSummaryData, setCarePlanSummaryData] = useState<any>(null);
  const [carePlanResponsiblePartiesData, setCarePlanResponsiblePartiesData] = useState<any>(null);
  const [carePlanCommunicationData, setCarePlanCommunicationData] = useState<any>(null);
  const [carePlanMedicationManagementData, setCarePlanMedicationManagementData] = useState<any>(null);
  const [carePlanHealthIndicatorData, setCarePlanHealthIndicatorData] = useState<any>(null);
  const [carePlanBodyChartData, setCarePlanBodyChartData] = useState<any>(null);
  const [carePlanInstrumentalActivitiesData, setCarePlanInstrumentalActivitiesData] = useState<any>(null);
  const [carePlanActivitiesOfDailyLivingData, setCarePlanActivitiesOfDailyLivingData] = useState<any>(null);
  const [carePlanUniversalPrecautionsData, setCarePlanUniversalPrecautionsData] = useState<any>(null);
  const [carePlanLeftAloneStatusData, setCarePlanLeftAloneStatusData] = useState<any>(null);
  const [carePlanPsychSocialCognitiveData, setCarePlanPsychSocialCognitiveData] = useState<any>(null);
  const [carePlanTreatmentProgramTherapiesData, setCarePlanTreatmentProgramTherapiesData] = useState<any>(null);
  const [facilityDocumentConfig, setFacilityDocumentConfig] = useState<ResidentCarePlanDocumentBundle["facilityDocumentConfig"]>(null);
  const [showFacilityDocumentDraftDialog, setShowFacilityDocumentDraftDialog] = useState<boolean>(false);
  const [showFacilityDocumentBatchDialog, setShowFacilityDocumentBatchDialog] = useState<boolean>(false);
  const [activeFacilityDocumentDraftConfig, setActiveFacilityDocumentDraftConfig] = useState<ResidentCarePlanDocumentConfig | null>(null);
  const [activeFacilityDocumentDraft, setActiveFacilityDocumentDraft] = useState<ResidentFacilityDocumentDraftBundle | null>(null);
  const [loadingFacilityDocumentDraft, setLoadingFacilityDocumentDraft] = useState<boolean>(false);
  const [savingFacilityDocumentDraft, setSavingFacilityDocumentDraft] = useState<boolean>(false);

  const [generatingCarePlanDocument, setGeneratingCarePlanDocument] = useState<boolean>(false);
  const [generatingBehaviorInterventionsDocument, setGeneratingBehaviorInterventionsDocument] = useState<boolean>(false);
  const [generatingPreliminaryServicePlanDocument, setGeneratingPreliminaryServicePlanDocument] = useState<boolean>(false);
  const [generatingConfiguredDocuments, setGeneratingConfiguredDocuments] = useState<Record<string, boolean>>({});
  const [carePlanDocumentDownloadLink, setCarePlanDocumentDownloadLink] = useState<string>("");
  const [carePlanTotalTimeTaken, setCarePlanTotalTimeTaken] = useState<number>(0);

  const [showHelpDialog, setShowHelpDialog] = useState<boolean>(false);
  const [helpMessage, setHelpMessage] = useState<string>("");
  const [isUserSubscribed, setIsUserSubscribed] = useState<boolean>(true);
  const [showNcpAccessDialog, setShowNcpAccessDialog] = useState<boolean>(false);
  const [ncpAccessDialogMessage, setNcpAccessDialogMessage] = useState<string>("");
  const [ncpAccessDialogForSubscribedUser, setNcpAccessDialogForSubscribedUser] = useState<boolean>(false);
  const [ncpAccessDialogAvailableCredits, setNcpAccessDialogAvailableCredits] = useState<number>(0);
  const [ncpAccessDialogAllowBillingSubscription, setNcpAccessDialogAllowBillingSubscription] = useState<boolean>(true);
  const [ncpAccessDialogAllowBillingCredits, setNcpAccessDialogAllowBillingCredits] = useState<boolean>(true);
  const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState<boolean>(false);
  const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState<string>("");
  const [featureAccessDialogAvailableCredits, setFeatureAccessDialogAvailableCredits] = useState<number>(0);
  const [featureAccessDialogForSubscribedUser, setFeatureAccessDialogForSubscribedUser] = useState<boolean>(false);

  const [showAIGeneratedNCPHelpDialog, setShowAIGeneratedNCPHelpDialog] = useState<boolean>(false);
  const [profileSetupMode, setProfileSetupMode] = useState<boolean>(false);
  const [showBehaviorInterventionImportDialog, setShowBehaviorInterventionImportDialog] = useState<boolean>(false);

  const [selectedAssessmentFile, setSelectedAssessmentFile] = useState<File | null>(null);
  const [residentBinder, setResidentBinder] = useState<Binder | null>(null);
  const [residentBinderFolders, setResidentBinderFolders] = useState<BinderFolder[]>([]);
  const [residentBinderDocuments, setResidentBinderDocuments] = useState<BinderDocument[]>([]);
  const [sharedResidentBinderAccessDenied, setSharedResidentBinderAccessDenied] = useState<boolean>(false);
  const canUseSharedResidentBinder = !isSharedResidentMode || !sharedResidentBinderAccessDenied;
  const [selectedBinderFolderId, setSelectedBinderFolderId] = useState<string>("");
  const [selectedBinderAssessmentDocumentId, setSelectedBinderAssessmentDocumentId] = useState<string>("");
  const [uploadingAssessment, setUploadingAssessment] = useState<boolean>(false);
  const [processingAssessment, setProcessingAssessment] = useState<boolean>(false);
  const [assessmentDialogError, setAssessmentDialogError] = useState<{ summary: string; detail: string } | null>(null);
  const [assessmentJobDetails, setAssessmentJobDetails] = useState<any>(null);
  const [assessmentProcessingTime, setAssessmentProcessingTime] = useState<number>(0);
  const [showJobSummary, setShowJobSummary] = useState<boolean>(false);
  const [ncpJobCompletedSuccessfully, setNcpJobCompletedSuccessfully] = useState<boolean>(false);
  const [selectedBehaviorInterventionFiles, setSelectedBehaviorInterventionFiles] = useState<File[]>([]);
  const [selectedBinderBehaviorDocumentIds, setSelectedBinderBehaviorDocumentIds] = useState<string[]>([]);
  const [uploadingBehaviorInterventionSources, setUploadingBehaviorInterventionSources] = useState<boolean>(false);
  const [processingBehaviorInterventionImport, setProcessingBehaviorInterventionImport] = useState<boolean>(false);
  const [behaviorInterventionImportJobDetails, setBehaviorInterventionImportJobDetails] = useState<any>(null);
  const [behaviorInterventionImportProcessingTime, setBehaviorInterventionImportProcessingTime] = useState<number>(0);
  const [behaviorInterventionImportCompletedSuccessfully, setBehaviorInterventionImportCompletedSuccessfully] = useState<boolean>(false);
  const [applyingBehaviorInterventionImport, setApplyingBehaviorInterventionImport] = useState<boolean>(false);
  const [showFacilityDocumentJobDialog, setShowFacilityDocumentJobDialog] = useState<boolean>(false);
  const [facilityDocumentJobConfig, setFacilityDocumentJobConfig] = useState<ResidentCarePlanDocumentConfig | null>(null);
  const [facilityDocumentJobDetails, setFacilityDocumentJobDetails] = useState<any>(null);
  const [facilityDocumentProcessingTime, setFacilityDocumentProcessingTime] = useState<number>(0);
  const [latestFacilityDocumentJobs, setLatestFacilityDocumentJobs] = useState<Record<string, any>>({});
  const [facilityDocumentPostAction, setFacilityDocumentPostAction] = useState<FacilityDocumentPostAction | null>(null);
  const [activeFacilityDocumentActionTarget, setActiveFacilityDocumentActionTarget] = useState<FacilityDocumentActionTarget | null>(null);
  const [showFacilityDocumentSourceDialog, setShowFacilityDocumentSourceDialog] = useState<boolean>(false);
  const [facilityDocumentSourceDialogConfig, setFacilityDocumentSourceDialogConfig] = useState<ResidentCarePlanDocumentConfig | null>(null);
  const [selectedFacilityDocumentSourceFile, setSelectedFacilityDocumentSourceFile] = useState<File | null>(null);
  const [facilityDocumentSourceDialogError, setFacilityDocumentSourceDialogError] = useState<string>("");
  const [processingFacilityDocumentBatch, setProcessingFacilityDocumentBatch] = useState<boolean>(false);
  const [facilityDocumentBatchJobDetails, setFacilityDocumentBatchJobDetails] = useState<any>(null);
  const [facilityDocumentBatchProcessingTime, setFacilityDocumentBatchProcessingTime] = useState<number>(0);

  const [doesNCPExtractedTextExist, setDoesNCPExtractedTextExist] = useState<boolean>(false);
  // last job details for assessment to NCP generation
  const [showLastJobDetailsDialog, setShowLastJobDetailsDialog] = useState<boolean>(false);
  const [lastJobDetails, setLastJobDetails] = useState<any>(null);
  const [showLastJobRetryButton, setShowLastJobRetryButton] = useState<boolean>(false);

  // NCP generated successfully
  const [ncpGeneratedSuccessfully, setNcpGeneratedSuccessfully] = useState<boolean>(false);
  const [lastNCPGenerationDate, setLastNCPGenerationDate] = useState<string>("");

  // Last asessment details
  const [lastAssessmentDate, setLastAssessmentDate] = useState<string>("");

  // compare assessment
  const [showCompareAssessmentPopUp, setShowCompareAssessmentPopUp] = useState<boolean>(false);
  const [nextAssessmentNCPGenerated, setNextAssessmentNCPGenerated] = useState<boolean>(false);
  const [doesNextNCPExtractedTextExist, setDoesNextNCPExtractedTextExist] = useState<boolean>(false);
  const [compareAssessmentSummaryData, setCompareAssessmentSummaryData] = useState<any>(null);
  const [processingCompareAssessmentSummary, setProcessingCompareAssessmentSummary] = useState<boolean>(false);
  const [doesCompareAssessmentSummaryExist, setDoesCompareAssessmentSummaryExist] = useState<boolean>(false);

  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [checkingArchivedResidentRestore, setCheckingArchivedResidentRestore] = useState<boolean>(false);
  const [restoringArchivedResident, setRestoringArchivedResident] = useState<boolean>(false);
  const [canRestoreArchivedResident, setCanRestoreArchivedResident] = useState<boolean>(true);
  const [archivedResidentRestoreMessage, setArchivedResidentRestoreMessage] = useState<string>("");
  const isAssessmentJobCompleted = String(assessmentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED;
  const isAssessmentReadyForCarePlan = doesNCPExtractedTextExist || ncpJobCompletedSuccessfully || isAssessmentJobCompleted;

  const toast = useRef<Toast>(null);
  const autoSaveHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResidentBootstrapRequestKeyRef = useRef<string>("");
  const hasTriggeredFacilityDocumentBatchRef = useRef<boolean>(false);
  const residentIsArchived = residentProfileData?.active === false;
  const facilityDocumentJobCompletedSuccessfully = String(facilityDocumentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED;
  const facilityDocumentJobFailed = String(facilityDocumentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.FAILED;
  const facilityDocumentBatchCompletedSuccessfully = String(facilityDocumentBatchJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED;
  const facilityDocumentBatchFailed = String(facilityDocumentBatchJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.FAILED;
  const isOneHubForCareBrand = authExperience.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE;
  const canAutoGenerateEsfDocumentsFromAssessment = isOneHubForCareBrand && String(facilityDocumentConfig?.appliedProfileKey || "").trim().toLowerCase() === "esf";
  const mergeLatestFacilityDocumentJobs = useCallback((jobs: any[]) => {
    const nextJobs: Record<string, any> = {};
    (Array.isArray(jobs) ? jobs : []).forEach((job) => {
      const documentType = String(
        job?.generatedDocument?.documentType
        || job?.documentType
        || ""
      ).trim().toLowerCase();
      if (!documentType) {
        return;
      }
      const existingJob = nextJobs[documentType];
      if (!existingJob || String(job?.createdAt || "").localeCompare(String(existingJob?.createdAt || "")) > 0) {
        nextJobs[documentType] = job;
      }
    });
    setLatestFacilityDocumentJobs(nextJobs);
    return nextJobs;
  }, []);

  const reloadCurrentResidentNcpPage = useCallback(() => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  const formatEstimatedSeconds = useCallback((totalSeconds: number) => {
    const normalizedSeconds = Math.max(0, Math.round(Number(totalSeconds || 0)));
    if (normalizedSeconds < 60) {
      return `About ${normalizedSeconds} seconds remaining`;
    }
    const minutes = Math.ceil(normalizedSeconds / 60);
    return `About ${minutes} minute${minutes === 1 ? "" : "s"} remaining`;
  }, []);

  const refreshLatestResidentJobs = useCallback(async () => {
    if (!residentId) {
      return;
    }
    try {
      const lastJobsResponse = await backendAPIStore.getLastJobs(residentId);
      if (lastJobsResponse?.jobs?.length) {
        setLastJobDetails(lastJobsResponse.jobs[0]);
        mergeLatestFacilityDocumentJobs(lastJobsResponse.jobs || []);
      }
    } catch (error) {
      console.error("Unable to refresh recent resident jobs", error);
    }
  }, [backendAPIStore, residentId, mergeLatestFacilityDocumentJobs]);

  const checkArchivedResidentRestoreAvailability = useCallback(async () => {
    if (authStore.isAdmin()) {
      setCanRestoreArchivedResident(true);
      setArchivedResidentRestoreMessage("");
      return true;
    }

    setCheckingArchivedResidentRestore(true);
    try {
      await backendAPIStore.init();
      const [residentCountResponse, subscriptionDetails] = await Promise.all([
        backendAPIStore.getResidentCountsCreatedBy(),
        backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER),
      ]);
      const activeResidentCount = Number(residentCountResponse?.count || 0);
      const allowedResidents = Number(subscriptionDetails?.allowedResidents || 0);
      const hasRoom = allowedResidents > activeResidentCount;
      setCanRestoreArchivedResident(hasRoom);
      setArchivedResidentRestoreMessage(
        hasRoom
          ? ""
          : "No resident slots are available right now. Free up a slot or update billing before restoring this resident.",
      );
      return hasRoom;
    } catch (error: any) {
      setCanRestoreArchivedResident(false);
      setArchivedResidentRestoreMessage(error?.message || "Unable to verify resident slot availability right now.");
      return false;
    } finally {
      setCheckingArchivedResidentRestore(false);
    }
  }, [authStore, backendAPIStore]);

  const restoreArchivedResident = async () => {
    if (!residentProfileData?.residentId) {
      return;
    }

    const canRestore = await checkArchivedResidentRestoreAvailability();
    if (!canRestore) {
      return;
    }

    setRestoringArchivedResident(true);
    try {
      await backendAPIStore.init();
      const updatedResidentProfile = await backendAPIStore.updateResident({
        ...residentProfileData,
        active: true,
      });
      setResidentProfileData(updatedResidentProfile || {
        ...residentProfileData,
        active: true,
      });
      setArchivedResidentRestoreMessage("");
      setCanRestoreArchivedResident(true);
      setUpdateFlag((previousValue) => !previousValue);
      toast.current?.show({ severity: "success", summary: "Resident Restored", detail: "Resident was restored to the active list.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to restore resident.", life: 3000 });
    } finally {
      setRestoringArchivedResident(false);
    }
  };

  const applyResidentCarePlanData = (residentCarePlanData: any[]) => {
    setCarePlanSummaryData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY)[0]);
    setCarePlanResponsiblePartiesData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES)[0]);
    setCarePlanCommunicationData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.COMMUNICATION)[0]);
    setCarePlanMedicationManagementData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT)[0]);
    setCarePlanHealthIndicatorData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS)[0]);
    setCarePlanBodyChartData(residentCarePlanData.filter((item: any) => item.carePlanType === BODY_CHART_CARE_PLAN_TYPE)[0]);
    setCarePlanInstrumentalActivitiesData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING)[0]);
    setCarePlanActivitiesOfDailyLivingData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING)[0]);
    setCarePlanUniversalPrecautionsData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS)[0]);
    setCarePlanLeftAloneStatusData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS)[0]);
    setCarePlanPsychSocialCognitiveData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE)[0]);
    setCarePlanTreatmentProgramTherapiesData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES)[0]);

    setDoesCarePlanExist(residentCarePlanData && residentCarePlanData.length > 0);

    if (residentCarePlanData && residentCarePlanData.length > 0) {
      const summaryData = residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY)[0];
      setLastAssessmentDate(summaryData?.assessmentDate || "");
    }
  };


  useEffect(() => {
    const loadResidentDetails = async () => {
      const requestKey = `${residentId || ""}|${updateFlag ? "1" : "0"}|${ncpJobCompletedSuccessfully ? "1" : "0"}`;
      if (lastResidentBootstrapRequestKeyRef.current === requestKey) {
        return;
      }
      lastResidentBootstrapRequestKeyRef.current = requestKey;
      setLoadingProfile(true);
      try {
        await backendAPIStore.init();
        const [residentProfileData, residentCarePlanBundle] = await Promise.all([
          backendAPIStore.getResidentDetails(residentId!),
          backendAPIStore.getResidentCarePlanBundle(residentId!) as Promise<ResidentCarePlanDocumentBundle>,
        ]);
        setResidentProfileData(residentProfileData);
        setDoesNCPExtractedTextExist(!!residentProfileData.nCPExtractedTextS3Key);
        setNcpGeneratedSuccessfully(residentProfileData.ncpGenerated);

        setLastNCPGenerationDate(residentProfileData.lastNCPGenerationDate);

        setNextAssessmentNCPGenerated(residentProfileData.nextAssessmentNCPGenerated);
        setDoesNextNCPExtractedTextExist(!!residentProfileData.nextNCPExtractedTextS3Key);
        setDoesCompareAssessmentSummaryExist(residentProfileData.compareAssessmentSummaryCompleted);
        setFacilityDocumentConfig(residentCarePlanBundle?.facilityDocumentConfig || null);
        applyResidentCarePlanData(residentCarePlanBundle?.items || []);
      } catch (error: any) {
        const normalizedMessage = String(error?.message || "").trim().toLowerCase();
        if (isSharedResidentMode && normalizedMessage.includes("permission denied") && ncpJobCompletedSuccessfully) {
          console.info("Skipping transient shared resident bootstrap refresh after NCP completion.", {
            residentId,
            homeId,
          });
          return;
        }
        lastResidentBootstrapRequestKeyRef.current = "";
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoadingProfile(false);
      }
    };
    loadResidentDetails();
  }, [updateFlag, residentId, backendAPIStore, authStore, ncpJobCompletedSuccessfully]);

  useEffect(() => {
    if (loadingProfile || !residentId) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const hydrateDeferredResidentData = async () => {
        try {
          const shouldLoadSubscription = authStore.getUserRoleType() === "provider" && !authStore.isAdmin();
          const [binderResult, lastJobsResult, subscriptionResult] = await Promise.allSettled([
            loadResidentBinderData(),
            backendAPIStore.getLastJobs(residentId),
            shouldLoadSubscription
              ? backendAPIStore.getSubscription(authStore.getUserId(), "provider")
              : Promise.resolve(null),
          ]);

          if (binderResult.status === "rejected") {
            console.error("Error loading resident binder bundle", binderResult.reason);
          }

          if (lastJobsResult.status === "fulfilled" && lastJobsResult.value.jobs && lastJobsResult.value.jobs.length > 0) {
            setLastJobDetails(lastJobsResult.value.jobs[0]);
            const failedNcp = lastJobsResult.value.jobs[0].ncpSections && lastJobsResult.value.jobs[0].ncpSections.some((section: any) => !section.success);
            setShowLastJobRetryButton(failedNcp);
            mergeLatestFacilityDocumentJobs(lastJobsResult.value.jobs || []);
          } else if (lastJobsResult.status === "rejected") {
            console.error("Error loading recent resident jobs", lastJobsResult.reason);
          }

          if (subscriptionResult.status === "fulfilled" && shouldLoadSubscription) {
            setIsUserSubscribed(!!subscriptionResult.value?.isSubscribed);
          } else if (subscriptionResult.status === "rejected") {
            console.error("Error loading subscription details for resident NCP page", subscriptionResult.reason);
          }
        } catch (error: any) {
          console.error("Error hydrating deferred resident NCP data", error);
        }
      };

      void hydrateDeferredResidentData();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [loadingProfile, residentId, backendAPIStore, authStore, updateFlag, ncpJobCompletedSuccessfully, mergeLatestFacilityDocumentJobs]);

  useEffect(() => {
    if (!residentIsArchived) {
      setCanRestoreArchivedResident(true);
      setArchivedResidentRestoreMessage("");
      return;
    }

    void checkArchivedResidentRestoreAvailability();
  }, [checkArchivedResidentRestoreAvailability, residentIsArchived]);

  useEffect(() => {
    const launchState = (location.state || {}) as { openAssessmentToNcp?: boolean; profileSetup?: boolean };
    const shouldOpenAssessmentDialog = !!launchState.openAssessmentToNcp;

    if (!shouldOpenAssessmentDialog || loadingProfile) {
      return;
    }

    setProfileSetupMode(launchState.profileSetup === true);
    setShowAIGeneratedNCPHelpDialog(true);

    navigate(location.pathname, { replace: true, state: {} });
  }, [loadingProfile, location.pathname, location.state, navigate, residentProfileData]);

  useEffect(() => {
    const getIndicatorTarget = (fieldName?: string) => {
      if (fieldName) {
        const byId = document.getElementById(fieldName);
        if (byId) {
          return byId as HTMLElement;
        }

        const byName = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
        if (byName) {
          return byName;
        }
      }

      return document.activeElement instanceof HTMLElement ? document.activeElement : null;
    };

    const handleAutoSaveStatus = (event: Event) => {
      const customEvent = event as CustomEvent<{
        state: "saving" | "saved";
        fieldName?: string;
        timestamp?: string;
      }>;
      const target = getIndicatorTarget(customEvent.detail.fieldName);
      const rect = target?.getBoundingClientRect();

      if (autoSaveHideTimeoutRef.current) {
        clearTimeout(autoSaveHideTimeoutRef.current);
      }

      setFieldAutoSaveIndicator({
        visible: true,
        top: rect ? Math.max(16, rect.top + Math.min(rect.height / 2, 24)) : 120,
        left: rect ? Math.min(window.innerWidth - 190, rect.right + 12) : window.innerWidth - 200,
        message:
          customEvent.detail.state === "saving"
            ? "Autosaving..."
            : `Saved${customEvent.detail.timestamp ? ` at ${customEvent.detail.timestamp}` : ""}`,
        state: customEvent.detail.state,
      });

      if (customEvent.detail.state === "saved") {
        autoSaveHideTimeoutRef.current = setTimeout(() => {
          setFieldAutoSaveIndicator((prev) => ({ ...prev, visible: false }));
        }, 2200);
      }
    };

    window.addEventListener("careplan-autosave-status", handleAutoSaveStatus);

    return () => {
      window.removeEventListener("careplan-autosave-status", handleAutoSaveStatus);
      if (autoSaveHideTimeoutRef.current) {
        clearTimeout(autoSaveHideTimeoutRef.current);
      }
    };
  }, []);

  const getResidentCarePlanData = async () => {
    try {
      const residentCarePlanBundle = await backendAPIStore.getResidentCarePlanBundle(
        residentId!,
      ) as ResidentCarePlanDocumentBundle;
      setFacilityDocumentConfig(residentCarePlanBundle?.facilityDocumentConfig || null);
      applyResidentCarePlanData(residentCarePlanBundle?.items || []);
    } catch (error: any) {
      const normalizedMessage = String(error?.message || "").trim().toLowerCase();
      if (isSharedResidentMode && normalizedMessage.includes("permission denied")) {
        console.info("Skipping transient shared resident care plan refresh after job completion.", {
          residentId,
          homeId,
        });
        return;
      }
      throw error;
    }
  };

  const applyDefaultBinderAssessmentSelection = (folders: BinderFolder[], documents: BinderDocument[]) => {
    const assessmentFolder = getBinderFolderByCode(folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
    const foldersWithFiles = folders.filter((folder) => getFolderDocuments(documents, folder.folderId).length > 0);
    const defaultFolder = foldersWithFiles.find((folder) => folder.folderId === assessmentFolder?.folderId) || foldersWithFiles[0];
    const defaultFolderDocuments = getFolderDocuments(documents, defaultFolder?.folderId);
    const defaultAssessmentDocument = getPrimaryDocument(defaultFolderDocuments);

    setSelectedBinderFolderId(defaultFolder?.folderId || "");
    setSelectedBinderAssessmentDocumentId(defaultAssessmentDocument?.binderDocumentId || "");
  };

  const loadResidentBinderData = async () => {
    if (!residentId) {
      return;
    }

    try {
      const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
      setSharedResidentBinderAccessDenied(false);
      setResidentBinder(bundle.binder);
      setResidentBinderFolders(bundle.folders);
      setResidentBinderDocuments(bundle.documents);
      applyDefaultBinderAssessmentSelection(bundle.folders, bundle.documents);
    } catch (error: any) {
      const normalizedMessage = String(error?.message || "").trim().toLowerCase();
      if (isSharedResidentMode && normalizedMessage.includes("permission denied")) {
        setSharedResidentBinderAccessDenied(true);
        setResidentBinder(null);
        setResidentBinderFolders([]);
        setResidentBinderDocuments([]);
        setSelectedBinderAssessmentDocumentId("");
        return;
      }
      throw error;
    }
  };

  const getResidentBinderFolder = (folderCode: string) => {
    return getBinderFolderByCode(residentBinderFolders, folderCode);
  };

  const getDocumentLoadingState = useCallback((documentType: string) => {
    if (documentType === "negotiated_care_plan") {
      return generatingCarePlanDocument;
    }
    if (documentType === "behavior_interventions_care_plan") {
      return generatingBehaviorInterventionsDocument;
    }
    if (documentType === "preliminary_service_care_plan") {
      return generatingPreliminaryServicePlanDocument;
    }
    return !!generatingConfiguredDocuments[documentType];
  }, [
    generatingBehaviorInterventionsDocument,
    generatingCarePlanDocument,
    generatingConfiguredDocuments,
    generatingPreliminaryServicePlanDocument,
  ]);

  const saveGeneratedObjectKeyToResidentBinder = async (input: {
    folderCode: string;
    title: string;
    fileName: string;
    objectKey: string;
    mimeType: string;
  }) => {
    if (!residentId) {
      return;
    }
    if (isSharedResidentMode && sharedResidentBinderAccessDenied) {
      throw new Error("Binder access is not enabled for this shared resident.");
    }

    const bundle = residentBinder?.binderId
      ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
      : await ensureResidentBinderBundle(backendAPIStore, residentId);
    const targetFolder = getBinderFolderByCode(bundle.folders, input.folderCode);
    if (!bundle.binder?.binderId || !targetFolder?.folderId) {
      throw new Error("Resident binder folder is not available.");
    }

    await upsertBinderDocumentWithVersion({
      backendAPIStore,
      binderId: bundle.binder.binderId,
      folderId: targetFolder.folderId,
      folderName: targetFolder.folderName || input.title,
      referenceId: residentId,
      title: input.title,
      fileName: input.fileName,
      mimeType: input.mimeType,
      existingObjectKey: input.objectKey,
      existingDocuments: bundle.documents,
    });

    await loadResidentBinderData();
  };

  const formatFacilityDocumentGeneratedAt = (value?: string | null) => {
    const normalized = String(value || "").trim();
    if (!normalized) {
      return "";
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return normalized;
    }
    return parsed.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const saveCompletedFacilityDocumentJobToBinder = useCallback(async (
    documentConfig: ResidentCarePlanDocumentConfig,
    jobDetails: any,
  ) => {
    const outputKey = String(jobDetails?.generatedDocument?.outputKey || "").trim();
    if (!outputKey) {
      throw new Error("The generated document could not be saved because no output file was returned.");
    }

    await saveGeneratedObjectKeyToResidentBinder({
      folderCode: documentConfig.binderFolderCode || RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
      title: documentConfig.title,
      fileName: String(jobDetails?.generatedDocument?.fileName || `${documentConfig.title.replace(/[^a-zA-Z0-9]+/g, "")}_${new Date().toISOString()}.docx`).trim(),
      objectKey: outputKey,
      mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }, [saveGeneratedObjectKeyToResidentBinder]);

  const getCompletedFacilityDocumentJob = useCallback((documentType: string) => {
    const latestJob = latestFacilityDocumentJobs[String(documentType || "").trim()];
    if (!latestJob) {
      return null;
    }
    return String(latestJob?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED
      ? latestJob
      : null;
  }, [latestFacilityDocumentJobs]);

  const formatBinderDocumentOptionLabel = (binderDocument: BinderDocument) => {
    const pieces = [binderDocument.title || "Untitled"];
    if (binderDocument.isPrimaryDocument) {
      pieces.push("Current");
    }
    if (binderDocument.latestVersionNumber) {
      pieces.push(`v${binderDocument.latestVersionNumber}`);
    }
    return pieces.join(" • ");
  };

  const initializeDefaultCarePlanData = async () => {
    const updatedCarePlanData = {
      ...DEFAULT_SUMMARY_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedCarePlanData);

    const responsiblePartiesData = { carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES };
    const updatedResponsiblePartiesData = {
      ...responsiblePartiesData,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedResponsiblePartiesData);

    const updatedCommunicationData = {
      ...DEFAULT_COMMUNICATION_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedCommunicationData);

    const updatedMedicationManagementData = {
      ...DEFAULT_MEDICATION_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedMedicationManagementData);

    const updatedHealthIndicatorData = {
      ...DEFAULT_HEALTH_INDICATOR_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedHealthIndicatorData);

    const updatedBodyChartData = {
      ...DEFAULT_BODY_CHART_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedBodyChartData);

    const updatedInstrumentalActivitiesData = {
      ...DEFAULT_INSTRUMENTAL_ACTIVITY_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedInstrumentalActivitiesData);

    const updatedActivitiesOfDailyLivingData = {
      ...DEFAULT_ACTIVITY_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedActivitiesOfDailyLivingData);

    const updatedLeftAloneStatusData = {
      ...DEFAULT_LEFT_ALONE_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedLeftAloneStatusData);

    const updatedPsychSocialCognitiveData = {
      ...DEFAULT_PSYCH_SOCIAL_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedPsychSocialCognitiveData);

    const updatedTreatmentProgramTherapiesData = {
      ...DEFAULT_TREATMENT_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedTreatmentProgramTherapiesData);

    const updatedUniversalPrecautionsData = {
      ...DEFAULT_UNIVERSAL_PRECAUTIONS_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedUniversalPrecautionsData);

    await getResidentCarePlanData();
    setDoesCarePlanExist(true);
  }

  const showGenerateNCPDocumentConfirmation = () => {
    if (!canUseSharedResidentBinder) {
      void generateCarePlanDocument(false);
      toast.current?.show({ severity: "info", summary: "Binder access needed", detail: "Binder access is not enabled for this shared resident, so the document was downloaded instead.", life: 4000 });
      return;
    }
    confirmDialog({
      message: renderConfirmMessage(
        "Generate care plan document",
        "Choose whether to download the care plan document or save it directly to the resident binder.",
      ),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: "Download",
      rejectClassName: "p-button-info p-button-outlined p-button-rounded",
      acceptLabel: "Save to Binder",
      acceptClassName: appConfirmDialogClasses.acceptClassName,
      accept: async () => {
        await generateCarePlanDocument(true);
      },
      reject: async () => {
        await generateCarePlanDocument(false);
      }
    });
  }

  const waitForResidentCarePlanData = useCallback(async (maxAttempts: number = 6, delayMs: number = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const residentCarePlanData = await backendAPIStore.getResidentCarePlan(residentId!);
      if (Array.isArray(residentCarePlanData) && residentCarePlanData.length > 0) {
        return residentCarePlanData;
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return [];
  }, [backendAPIStore, residentId]);

  const generateCarePlanDocument = useCallback(async (
    addToResidentProfile: boolean,
    options?: { initializeDefaultsIfMissing?: boolean },
  ) => {
    const startTime: number = Date.now();
    try {
      setGeneratingCarePlanDocument(true);
      if (options?.initializeDefaultsIfMissing !== false && !doesCarePlanExist) {
        await initializeDefaultCarePlanData();
      }
      await backendAPIStore.init();
      const responseNCP = await backendAPIStore.generateResidentCarePlanDocument(residentId!, homeId!);

      if (addToResidentProfile) {
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
          title: "Negotiated Care Plan",
          fileName: responseNCP.fileName || `NegotiatedCarePlan_${new Date().toISOString()}.docx`,
          objectKey: responseNCP.outputKey,
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      } else {
        openUrlInNewTab(responseNCP.documentUrl);
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      setGeneratingCarePlanDocument(false);
      const endTime: number = Date.now();
      setCarePlanTotalTimeTaken((endTime - startTime) / 1000); // time in seconds
      await getResidentCarePlanData();
    }
  }, [backendAPIStore, doesCarePlanExist, homeId, residentId, saveGeneratedObjectKeyToResidentBinder]);

  const autoSaveCompletedCarePlanDocumentForJob = useCallback(async (jobId: string) => {
    const normalizedJobId = String(jobId || "").trim();
    if (!normalizedJobId || autoSavedCompletedNcpJobIdsRef.current.has(normalizedJobId)) {
      return;
    }

    if (isSharedResidentMode) {
      return;
    }

    autoSavedCompletedNcpJobIdsRef.current.add(normalizedJobId);

    try {
      const latestCarePlanData = await waitForResidentCarePlanData();
      if (!Array.isArray(latestCarePlanData) || latestCarePlanData.length === 0) {
        throw new Error("The generated care plan is still syncing. Please open Generate Care Plan again in a moment to save it to the binder.");
      }

      applyResidentCarePlanData(latestCarePlanData);
      await generateCarePlanDocument(true, { initializeDefaultsIfMissing: false });
    } catch (error: any) {
      autoSavedCompletedNcpJobIdsRef.current.delete(normalizedJobId);
      toast.current?.show({
        severity: "warn",
        summary: "Binder save needed",
        detail: error?.message || "The care plan draft finished, but the document could not be saved to the resident binder automatically.",
        life: 5000,
      });
    }
  }, [generateCarePlanDocument, isSharedResidentMode, waitForResidentCarePlanData]);

  const triggerCompletedCarePlanBinderSave = useCallback((jobId: string) => {
    void autoSaveCompletedCarePlanDocumentForJob(jobId)
      .then(() => {
        void loadResidentBinderData();
      });
  }, [autoSaveCompletedCarePlanDocumentForJob, loadResidentBinderData]);

  const showGenerateBehaviorInterventionsDocumentConfirmation = () => {
    if (!canUseSharedResidentBinder) {
      void generateBehaviorInterventionsDocument(false);
      toast.current?.show({ severity: "info", summary: "Binder access needed", detail: "Binder access is not enabled for this shared resident, so the document was downloaded instead.", life: 4000 });
      return;
    }
    confirmDialog({
      message: renderConfirmMessage(
        "Generate behavior and interventions care plan",
        "Choose whether to download the behavior and interventions care plan or save it directly to the resident binder.",
      ),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: "Download",
      rejectClassName: "p-button-info p-button-outlined p-button-rounded",
      acceptLabel: "Save to Binder",
      acceptClassName: appConfirmDialogClasses.acceptClassName,
      accept: async () => {
        await generateBehaviorInterventionsDocument(true);
      },
      reject: async () => {
        await generateBehaviorInterventionsDocument(false);
      }
    });
  }

  const showGeneratePreliminaryServicePlanDocumentConfirmation = () => {
    if (!canUseSharedResidentBinder) {
      void generatePreliminaryServicePlanDocument(false);
      toast.current?.show({ severity: "info", summary: "Binder access needed", detail: "Binder access is not enabled for this shared resident, so the document was downloaded instead.", life: 4000 });
      return;
    }
    confirmDialog({
      message: renderConfirmMessage(
        "Generate preliminary service care plan",
        "Choose whether to download the preliminary service care plan or save it directly to the resident binder.",
      ),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: "Download",
      rejectClassName: "p-button-info p-button-outlined p-button-rounded",
      acceptLabel: "Save to Binder",
      acceptClassName: appConfirmDialogClasses.acceptClassName,
      accept: async () => {
        await generatePreliminaryServicePlanDocument(true);
      },
      reject: async () => {
        await generatePreliminaryServicePlanDocument(false);
      }
    });
  }

  const generateBehaviorInterventionsDocument = async (addToResidentProfile: boolean) => {
    try {
      setGeneratingBehaviorInterventionsDocument(true);
      if (!doesCarePlanExist) {
        await initializeDefaultCarePlanData();
      }
      await backendAPIStore.init();
      const response = await backendAPIStore.generateBehaviorInterventionsCarePlanDocument(residentId!, homeId!);

      if (addToResidentProfile) {
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
          title: "Behavior and Interventions Care Plan",
          fileName: response.fileName || `BehaviorInterventionsCarePlan_${new Date().toISOString()}.docx`,
          objectKey: response.outputKey,
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      } else {
        openUrlInNewTab(response.documentUrl);
      }
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        openSubscriptionFeatureAccessDialog(
          error?.message || error?.details?.error,
          !!error?.details?.usageLimit,
        );
      } else {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
      }
    } finally {
      setGeneratingBehaviorInterventionsDocument(false);
      await getResidentCarePlanData();
    }
  };

  const generatePreliminaryServicePlanDocument = async (addToResidentProfile: boolean) => {
    try {
      setGeneratingPreliminaryServicePlanDocument(true);
      if (!doesCarePlanExist) {
        await initializeDefaultCarePlanData();
      }
      await backendAPIStore.init();
      const response = await backendAPIStore.generatePreliminaryServiceCarePlanDocument(residentId!, homeId!);

      if (addToResidentProfile) {
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
          title: "Preliminary Service Care Plan",
          fileName: response.fileName || `PreliminaryServiceCarePlan_${new Date().toISOString()}.docx`,
          objectKey: response.outputKey,
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      } else {
        openUrlInNewTab(response.documentUrl);
      }
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        openSubscriptionFeatureAccessDialog(
          error?.message || error?.details?.error,
          !!error?.details?.usageLimit,
        );
      } else {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
      }
    } finally {
      setGeneratingPreliminaryServicePlanDocument(false);
      await getResidentCarePlanData();
    }
  };

  const pollFacilityDocumentJob = useCallback(async (
    documentConfig: ResidentCarePlanDocumentConfig,
    jobId: string,
  ) => {
    const normalizedJobId = String(jobId || "").trim();
    if (!normalizedJobId) {
      throw new Error("The document job did not return a valid job id.");
    }

    const startedAt = Date.now();
    let currentJobDetails: any = null;
    let currentStatus: string = JOB_STATUS.SUBMITTED;
    do {
      currentJobDetails = await backendAPIStore.getJobDetails(normalizedJobId);
      setFacilityDocumentJobDetails(currentJobDetails);
      currentStatus = String(currentJobDetails?.jobStatus || JOB_STATUS.SUBMITTED).trim().toUpperCase();
      setFacilityDocumentProcessingTime(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));

      if (currentStatus === JOB_STATUS.COMPLETED || currentStatus === JOB_STATUS.FAILED) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 2500));
    } while ((Date.now() - startedAt) / 1000 < 300);

    if (currentStatus !== JOB_STATUS.COMPLETED) {
      throw new Error(currentJobDetails?.errorMessage || "The document job did not finish successfully.");
    }

    setLatestFacilityDocumentJobs((current) => ({
      ...current,
      [documentConfig.documentType]: currentJobDetails,
    }));
    return currentJobDetails;
  }, [backendAPIStore]);

  const runConfiguredDocumentGenerationJob = useCallback(async (
    documentConfig: ResidentCarePlanDocumentConfig,
    options?: {
      sourceFileName?: string;
      fileContentBase64?: string;
    },
  ) => {
    try {
      setGeneratingConfiguredDocuments((current) => ({ ...current, [documentConfig.documentType]: true }));
      setActiveFacilityDocumentActionTarget({
        documentType: documentConfig.documentType,
        label: documentConfig.title,
      });
      setFacilityDocumentJobConfig(documentConfig);
      setFacilityDocumentJobDetails(null);
      setFacilityDocumentProcessingTime(0);
      setFacilityDocumentPostAction(null);
      setShowFacilityDocumentJobDialog(true);

      if (documentConfig.requiresCarePlanDraft && !doesCarePlanExist) {
        await initializeDefaultCarePlanData();
      }
      await backendAPIStore.init();
      const response = await backendAPIStore.generateConfiguredResidentCarePlanDocument(
        residentId!,
        homeId!,
        documentConfig.documentType,
        options,
      );

      if (response?.jobId) {
        await pollFacilityDocumentJob(documentConfig, String(response.jobId).trim());
      } else {
        const immediateJobDetails = {
          jobStatus: JOB_STATUS.COMPLETED,
          jobSubStatus: JOB_STATUS.COMPLETED,
          createdAt: new Date().toISOString(),
          completedAt: new Date().toISOString(),
          generatedDocument: {
            documentType: documentConfig.documentType,
            outputKey: String(response?.outputKey || "").trim(),
            fileName: String(response?.fileName || "").trim(),
            documentUrl: String(response?.documentUrl || "").trim(),
          },
        };
        setFacilityDocumentJobDetails(immediateJobDetails);
        setLatestFacilityDocumentJobs((current) => ({
          ...current,
          [documentConfig.documentType]: immediateJobDetails,
        }));
      }
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        setShowFacilityDocumentJobDialog(false);
        openSubscriptionFeatureAccessDialog(
          error?.message || error?.details?.error,
          !!error?.details?.usageLimit,
        );
      } else {
        setFacilityDocumentJobDetails((current: any) => current ? ({
          ...current,
          jobStatus: JOB_STATUS.FAILED,
          jobSubStatus: String(error?.message || "Document generation failed"),
          errorMessage: String(error?.message || "Document generation failed"),
        }) : null);
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate the document.", life: 5000 });
      }
    } finally {
      setGeneratingConfiguredDocuments((current) => ({ ...current, [documentConfig.documentType]: false }));
      setActiveFacilityDocumentActionTarget((current) =>
        current?.documentType === documentConfig.documentType ? null : current,
      );
      await getResidentCarePlanData();
    }
  }, [
    backendAPIStore,
    doesCarePlanExist,
    getResidentCarePlanData,
    homeId,
    initializeDefaultCarePlanData,
    openSubscriptionFeatureAccessDialog,
    pollFacilityDocumentJob,
    residentId,
  ]);

  const handleCompletedFacilityDocumentAction = useCallback(async (
    action: FacilityDocumentPostAction,
    documentConfig?: ResidentCarePlanDocumentConfig | null,
    jobDetails?: any,
    options?: { reloadAfter?: boolean },
  ) => {
    const resolvedConfig = documentConfig || facilityDocumentJobConfig;
    const resolvedJobDetails = jobDetails || facilityDocumentJobDetails;
    const shouldReloadAfter = !!options?.reloadAfter;
    if (!resolvedConfig || !resolvedJobDetails) {
      return;
    }

    try {
      setFacilityDocumentPostAction(action);
      setActiveFacilityDocumentActionTarget({
        documentType: resolvedConfig.documentType,
        label: resolvedConfig.title,
      });
      if (action === "binder" && !canUseSharedResidentBinder) {
        toast.current?.show({ severity: "info", summary: "Binder access needed", detail: "Binder access is not enabled for this shared resident. Download the document instead.", life: 4000 });
        return;
      }
      if (action === "binder") {
        await saveCompletedFacilityDocumentJobToBinder(resolvedConfig, resolvedJobDetails);
        toast.current?.show({ severity: "success", summary: "Saved to Binder", detail: `${resolvedConfig.title} was saved to the resident binder.`, life: 3000 });
        if (shouldReloadAfter) {
          reloadCurrentResidentNcpPage();
        }
      } else {
        const outputKey = String(resolvedJobDetails?.generatedDocument?.outputKey || "").trim();
        const fileName = String(
          resolvedJobDetails?.generatedDocument?.fileName
          || `${resolvedConfig.title.replace(/[^a-zA-Z0-9]+/g, "")}_${new Date().toISOString()}.docx`,
        ).trim();
        if (!residentId || !outputKey) {
          throw new Error("The generated document is ready, but no output file was returned.");
        }
        await backendAPIStore.init();
        const freshDownloadUrl = await backendAPIStore.getPresignedUrlForDownload(
          residentId,
          "resident",
          "",
          outputKey,
          fileName,
        );
        openUrlInNewTab(String(freshDownloadUrl || "").trim());
        if (shouldReloadAfter) {
          reloadCurrentResidentNcpPage();
        }
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to complete the document action.", life: 5000 });
    } finally {
      setFacilityDocumentPostAction(null);
      setActiveFacilityDocumentActionTarget((current) =>
        current?.documentType === resolvedConfig.documentType ? null : current,
      );
    }
  }, [backendAPIStore, canUseSharedResidentBinder, facilityDocumentJobConfig, facilityDocumentJobDetails, reloadCurrentResidentNcpPage, residentId, saveCompletedFacilityDocumentJobToBinder]);

  const generateConfiguredDocument = useCallback(async (
    documentConfig: ResidentCarePlanDocumentConfig,
    addToResidentProfile: boolean,
  ) => {
    try {
      setGeneratingConfiguredDocuments((current) => ({ ...current, [documentConfig.documentType]: true }));
      if (documentConfig.requiresCarePlanDraft && !doesCarePlanExist) {
        await initializeDefaultCarePlanData();
      }
      await backendAPIStore.init();
      const response = await backendAPIStore.generateConfiguredResidentCarePlanDocument(
        residentId!,
        homeId!,
        documentConfig.documentType,
      );

      if (response?.jobId) {
        const completedJobDetails = await pollFacilityDocumentJob(documentConfig, String(response.jobId).trim());
        await handleCompletedFacilityDocumentAction(addToResidentProfile ? "binder" : "download", documentConfig, completedJobDetails);
      } else if (addToResidentProfile) {
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: documentConfig.binderFolderCode || RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
          title: documentConfig.title,
          fileName: String(response?.fileName || `${documentConfig.title.replace(/[^a-zA-Z0-9]+/g, "")}_${new Date().toISOString()}.docx`).trim(),
          objectKey: String(response?.outputKey || "").trim(),
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      } else {
        openUrlInNewTab(String(response?.documentUrl || "").trim());
      }
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        openSubscriptionFeatureAccessDialog(
          error?.message || error?.details?.error,
          !!error?.details?.usageLimit,
        );
      } else {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate the document.", life: 5000 });
      }
    } finally {
      setGeneratingConfiguredDocuments((current) => ({ ...current, [documentConfig.documentType]: false }));
      await getResidentCarePlanData();
    }
  }, [
    backendAPIStore,
    doesCarePlanExist,
    getResidentCarePlanData,
    handleCompletedFacilityDocumentAction,
    homeId,
    initializeDefaultCarePlanData,
    openSubscriptionFeatureAccessDialog,
    pollFacilityDocumentJob,
    residentId,
    saveGeneratedObjectKeyToResidentBinder,
  ]);

  const openFacilityDocumentDraftDialog = async (documentConfig: ResidentCarePlanDocumentConfig) => {
    try {
      setActiveFacilityDocumentDraftConfig(documentConfig);
      setShowFacilityDocumentDraftDialog(true);
      setLoadingFacilityDocumentDraft(true);
      await backendAPIStore.init();
      const response = await backendAPIStore.getResidentFacilityDocumentDraft(
        residentId!,
        documentConfig.documentType,
      ) as ResidentFacilityDocumentDraftBundle;
      setActiveFacilityDocumentDraft(response);
    } catch (error: any) {
      setShowFacilityDocumentDraftDialog(false);
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load the document sections.", life: 5000 });
    } finally {
      setLoadingFacilityDocumentDraft(false);
    }
  };

  const updateFacilityDocumentDraftField = (sectionKey: string, fieldKey: string, value: string) => {
    setActiveFacilityDocumentDraft((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        sections: (current.sections || []).map((section) =>
          section.sectionKey !== sectionKey
            ? section
            : {
              ...section,
              fields: (section.fields || []).map((field) =>
                field.fieldKey !== fieldKey
                  ? field
                  : { ...field, value }
              ),
            }
        ),
      };
    });
  };

  const updateFacilityDocumentDraftSectionFields = (
    sectionKey: string,
    updater: (fields: ResidentFacilityDocumentDraftField[]) => ResidentFacilityDocumentDraftField[],
  ) => {
    setActiveFacilityDocumentDraft((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        sections: (current.sections || []).map((section) =>
          section.sectionKey !== sectionKey
            ? section
            : {
              ...section,
              fields: updater(section.fields || []),
            }
        ),
      };
    });
  };

  const updateChallengingBehaviorDraftRow = (
    sectionKey: string,
    rowId: number,
    fieldName: keyof Omit<ResidentChallengingBehaviorRow, "rowId">,
    value: string | string[],
  ) => {
    updateFacilityDocumentDraftSectionFields(sectionKey, (fields) => {
      const rows = extractChallengingBehaviorRows(fields).map((row) =>
        row.rowId !== rowId ? row : { ...row, [fieldName]: value },
      );
      return buildChallengingBehaviorFields(rows, fields);
    });
  };

  const addChallengingBehaviorDraftRow = (sectionKey: string) => {
    updateFacilityDocumentDraftSectionFields(sectionKey, (fields) => {
      const rows = extractChallengingBehaviorRows(fields);
      const nextRowId = rows.length ? Math.max(...rows.map((row) => row.rowId)) + 1 : 1;
      return buildChallengingBehaviorFields([
        ...rows,
        {
          rowId: nextRowId,
          behaviorKey: "",
          behaviorDetails: "",
          interventionKeys: [],
        },
      ], fields);
    });
  };

  const removeChallengingBehaviorDraftRow = (sectionKey: string, rowId: number) => {
    updateFacilityDocumentDraftSectionFields(sectionKey, (fields) => {
      const rows = extractChallengingBehaviorRows(fields);
      const nextRows = rows.filter((row) => row.rowId !== rowId);
      return buildChallengingBehaviorFields(nextRows, fields);
    });
  };

  const saveFacilityDocumentDraft = async () => {
    if (!activeFacilityDocumentDraft || !activeFacilityDocumentDraftConfig) {
      return;
    }
    try {
      setSavingFacilityDocumentDraft(true);
      await backendAPIStore.init();
      await backendAPIStore.saveResidentFacilityDocumentDraft(
        residentId!,
        homeId!,
        activeFacilityDocumentDraftConfig.documentType,
        activeFacilityDocumentDraft.sections || [],
      );
      toast.current?.show({ severity: "success", summary: "Sections Saved", detail: "The document sections were saved.", life: 3000 });
      setShowFacilityDocumentDraftDialog(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save the document sections.", life: 5000 });
    } finally {
      setSavingFacilityDocumentDraft(false);
    }
  };

  const openFacilityDocumentSourceDialog = useCallback((documentConfig: ResidentCarePlanDocumentConfig) => {
    setFacilityDocumentSourceDialogConfig(documentConfig);
    setSelectedFacilityDocumentSourceFile(null);
    setFacilityDocumentSourceDialogError("");
    setShowFacilityDocumentSourceDialog(true);
  }, []);

  const showGenerateConfiguredDocumentConfirmation = (documentConfig: ResidentCarePlanDocumentConfig) => {
    if (isEsfDocumentProfile) {
      openFacilityDocumentSourceDialog(documentConfig);
      return;
    }

    const latestJob = latestFacilityDocumentJobs[documentConfig.documentType];
    const latestCompletedAt = formatFacilityDocumentGeneratedAt(
      latestJob?.completedAt || latestJob?.createdAt,
    );

    if (latestJob && String(latestJob?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED) {
      confirmDialog({
        message: renderConfirmMessage(
          `Regenerate ${documentConfig.title.toLowerCase()}`,
          `You already generated this document on ${latestCompletedAt || "a previous run"}. Do you want to regenerate it?`,
        ),
        header: "Confirmation",
        icon: "pi pi-exclamation-triangle",
        rejectLabel: "Cancel",
        rejectClassName: "p-button-text p-button-rounded",
        acceptLabel: "Regenerate",
        acceptClassName: appConfirmDialogClasses.acceptClassName,
        accept: async () => {
          await runConfiguredDocumentGenerationJob(documentConfig);
        },
      });
      return;
    }

    void runConfiguredDocumentGenerationJob(documentConfig);
  };

  const handleFacilityDocumentSourceFileChange = useCallback((event: any) => {
    setFacilityDocumentSourceDialogError("");
    const file = event?.files?.[0] || null;
    if (!file) {
      setSelectedFacilityDocumentSourceFile(null);
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (file.size > RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES || !FACILITY_DOCUMENT_SOURCE_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid file",
        detail: `Please upload PDF, DOC, DOCX, TXT, or scanned image files smaller than ${RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES / (1024 * 1024)} MB.`,
        life: 5000,
      });
      setSelectedFacilityDocumentSourceFile(null);
      return;
    }

    setSelectedFacilityDocumentSourceFile(file);
  }, []);

  const submitFacilityDocumentSourceGeneration = useCallback(async (
    documentConfigOverride?: ResidentCarePlanDocumentConfig | null,
  ) => {
    const resolvedConfig = documentConfigOverride || facilityDocumentSourceDialogConfig;
    if (!resolvedConfig) {
      return;
    }

    try {
      setFacilityDocumentSourceDialogError("");
      const sourceOptions = selectedFacilityDocumentSourceFile
        ? {
          sourceFileName: selectedFacilityDocumentSourceFile.name,
          fileContentBase64: await readFileAsBase64(selectedFacilityDocumentSourceFile),
        }
        : undefined;

      setShowFacilityDocumentSourceDialog(false);
      await runConfiguredDocumentGenerationJob(resolvedConfig, sourceOptions);
    } catch (error: any) {
      setShowFacilityDocumentSourceDialog(true);
      setFacilityDocumentSourceDialogError(error?.message || "Unable to read the selected document.");
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to read the selected document.",
        life: 5000,
      });
    }
  }, [facilityDocumentSourceDialogConfig, runConfiguredDocumentGenerationJob, selectedFacilityDocumentSourceFile]);

  const configuredDocuments = useMemo(() => {
    const documents = facilityDocumentConfig?.documents;
    return Array.isArray(documents) ? documents : [];
  }, [facilityDocumentConfig]);

  const isEsfDocumentProfile = facilityDocumentConfig?.appliedProfileKey === "esf";
  const esfDocumentMenuItems = useMemo<MenuItem[]>(() => configuredDocuments.map((documentConfig) => {
    const completedJob = getCompletedFacilityDocumentJob(documentConfig.documentType);
    return {
      label: documentConfig.title,
      icon: "pi pi-file",
      items: [
        ...(completedJob ? [
          {
            label: "Download File",
            icon: "pi pi-download",
            command: () => {
              void handleCompletedFacilityDocumentAction("download", documentConfig, completedJob);
            },
          },
          {
            label: "Add to Binder",
            icon: "pi pi-folder-plus",
            command: () => {
              void handleCompletedFacilityDocumentAction("binder", documentConfig, completedJob);
            },
          },
        ] : []),
        ...(documentConfig.draftSchemaKey ? [{
          label: "Edit",
          icon: "pi pi-pencil",
          command: () => {
            void openFacilityDocumentDraftDialog(documentConfig);
          },
        }] : []),
        {
          label: "Upload Existing Document and Generate",
          icon: "pi pi-cog",
          command: () => {
            showGenerateConfiguredDocumentConfirmation(documentConfig);
          },
        },
      ],
    };
  }), [configuredDocuments, getCompletedFacilityDocumentJob, handleCompletedFacilityDocumentAction, showGenerateConfiguredDocumentConfirmation]);
  const showNcpAccessNeededDialog = (error: any) => {
    const backendMessage = String(
      error?.details?.error
      || error?.message
      || ""
    ).trim();
    const availableFeatureCredits = Number(
      error?.details?.automationCreditsAvailable
      || error?.details?.purchasedAutomationCreditsAvailable
      || error?.details?.featureCreditBalances?.provider_nurse_assessment
      || 0
    );
    const isSubscribedQuotaReached = !!error?.details?.usageLimit;
    const allowBillingSubscription = error?.details?.canSubscribe !== false;
    const allowBillingCredits = error?.details?.canBuyFeatureCredits !== false;
    const isSharedOwnerSubscriptionGate = isSharedResidentMode && !allowBillingSubscription && !allowBillingCredits;

    setNcpAccessDialogForSubscribedUser(isSubscribedQuotaReached);
    setNcpAccessDialogAvailableCredits(requiresProviderSubscriptionMessageOnly ? 0 : availableFeatureCredits);
    setNcpAccessDialogAllowBillingSubscription(requiresProviderSubscriptionMessageOnly ? false : allowBillingSubscription);
    setNcpAccessDialogAllowBillingCredits(requiresProviderSubscriptionMessageOnly ? false : allowBillingCredits);

    if (requiresProviderSubscriptionMessageOnly) {
      setNcpAccessDialogMessage("The provider must have an active subscription to use the shared resident care plan workspace.");
      setShowNcpAccessDialog(true);
      return;
    }

    if (isSharedOwnerSubscriptionGate && backendMessage) {
      setNcpAccessDialogMessage(backendMessage);
    } else if (!isUserSubscribed) {
      setNcpAccessDialogMessage(
        "An active subscription is required to continue with NCP automation."
      );
    } else if (isSubscribedQuotaReached) {
      setNcpAccessDialogMessage(
        availableFeatureCredits > 0
          ? `Billing-period NCP access is used up.\n\nUse 1 automation credit to continue.`
          : (backendMessage || "Buy automation credits to continue.")
      );
    } else if (availableFeatureCredits > 0) {
      setNcpAccessDialogMessage(
        backendMessage || "Use 1 automation credit to continue."
      );
    } else {
      setNcpAccessDialogMessage(
        backendMessage || "No free NCP access left.\n\nBuy automation credits to continue."
      );
    }

    setShowNcpAccessDialog(true);
  };

  const promptForCompareFeatureCredit = (
    error: any,
    _existingCreditAction: () => void,
  ) => {
    const option = {
      label: "Compare",
      limit: error?.details?.usageLimit || 5,
    };
    const currentPeriodEnd = error?.details?.currentPeriodEnd ? new Date(error.details.currentPeriodEnd) : null;
    const isSubscribedQuotaReached = !!error?.details?.usageLimit;

    setFeatureAccessDialogAvailableCredits(0);
    setFeatureAccessDialogForSubscribedUser(isSubscribedQuotaReached);

    if (isSubscribedQuotaReached) {
      setFeatureAccessDialogMessage(
        `You have used your ${option.limit} included ${option.label.toLowerCase()} tries for this billing period.\n\nYou will get ${option.limit} more tries starting ${currentPeriodEnd ? currentPeriodEnd.toLocaleDateString() : "on your next billing date"}.`
      );
    } else {
      setFeatureAccessDialogMessage(
        `${option.label} is now included through subscription only.\n\nSubscribe to continue using this feature.`
      );
    }

    setShowFeatureAccessDialog(true);
  };

  function openSubscriptionFeatureAccessDialog(
    message?: string,
    isSubscribedQuotaReached: boolean = false,
  ) {
    setFeatureAccessDialogAvailableCredits(0);
    setFeatureAccessDialogForSubscribedUser(isSubscribedQuotaReached);
    setFeatureAccessDialogMessage(
      String(message || "").trim()
      || (isSubscribedQuotaReached
        ? "You have reached the included usage for this feature."
        : "This feature is available through subscription.")
    );
    setShowFeatureAccessDialog(true);
  }

  const onViewLastJobDetailsClick = () => {
    setShowLastJobDetailsDialog(true);
  }

  const submitCarePlanData = async (carePlanData: any, options?: { showSuccessToast?: boolean }) => {
    try {
      setLoadingCarePlan(true);
      await backendAPIStore.init();
      const updatedCarePlanData = {
        ...carePlanData,
        residentId: residentId,
        homeId: homeId,
      };
      await backendAPIStore.updateResidentCarePlan(updatedCarePlanData);
      await getResidentCarePlanData();
      if (options?.showSuccessToast) {
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Care plan changes saved successfully.', life: 2500 });
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
    } finally {
      setLoadingCarePlan(false);
    }
  };

  const sectionHeaderTemplate = (title: string) => (
    renderSurfaceHeader(
      title,
      undefined,
      "pi pi-file-edit",
    )
  );

  const onDeactivateProfile = () => {
    confirmDialog({
      message: renderConfirmMessage(
        "Deactivate resident profile",
        "This archives the resident from the active list while keeping the underlying data for record-keeping and possible recovery.",
      ),
      icon: 'pi pi-exclamation-triangle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.deactivateResident(residentId!);
          toast.current?.show({ severity: 'success', summary: 'Profile Deactivated', detail: 'Resident profile has been deactivated.', life: 3000 });
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        }
      },
      reject: () => {
        // Do nothing on reject
      }
    });
  };

  const profileFooterTemplate = () => (
    <div className="flex flex-wrap justify-content-end gap-2">
      <Button icon="pi pi-folder-open" outlined rounded label="Binder" severity="warning" size="small"
        onClick={() => {
          navigate(residentBinderPath);
        }}
      />
    </div>
  );

  const showHelpMessage = (message: string) => {
    setHelpMessage(message);
    setShowHelpDialog(true);
  }

  const maybeStartEsfFacilityDocumentBatch = useCallback(async (options?: { requireAutoEligibility?: boolean }) => {
    const canStartEsfBatch = !!residentId && !!homeId && isEsfDocumentProfile;
    if (!canStartEsfBatch) {
      return;
    }
    if (options?.requireAutoEligibility && !canAutoGenerateEsfDocumentsFromAssessment) {
      return;
    }

    try {
      hasTriggeredFacilityDocumentBatchRef.current = true;
      setShowFacilityDocumentBatchDialog(true);
      const startTime = Date.now();
      setProcessingFacilityDocumentBatch(true);
      setFacilityDocumentBatchJobDetails(null);
      setFacilityDocumentBatchProcessingTime(0);
      await backendAPIStore.init();
      const batchJob = await backendAPIStore.generateResidentFacilityDocumentBatch(residentId, homeId);
      const normalizedJobId = String(batchJob?.jobId || "").trim();
      if (!normalizedJobId) {
        setProcessingFacilityDocumentBatch(false);
        return;
      }

      let currentJobDetails = await backendAPIStore.getJobDetails(normalizedJobId);
      setFacilityDocumentBatchJobDetails(currentJobDetails);
      let currentStatus = String(currentJobDetails?.jobStatus || JOB_STATUS.SUBMITTED).trim().toUpperCase();
      let midTime = Date.now();
      do {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        currentJobDetails = await backendAPIStore.getJobDetails(normalizedJobId);
        setFacilityDocumentBatchJobDetails(currentJobDetails);
        currentStatus = String(currentJobDetails?.jobStatus || JOB_STATUS.SUBMITTED).trim().toUpperCase();
        midTime = Date.now();
        setFacilityDocumentBatchProcessingTime(Math.round((midTime - startTime) / 1000));
      } while (
        currentStatus !== JOB_STATUS.COMPLETED
        && currentStatus !== JOB_STATUS.FAILED
        && (midTime - startTime) / 1000 < 1800
      );

      setProcessingFacilityDocumentBatch(false);
      await refreshLatestResidentJobs();
      if (currentStatus !== JOB_STATUS.COMPLETED && currentStatus !== JOB_STATUS.FAILED) {
        toast.current?.show({
          severity: "warn",
          summary: "Still processing",
          detail: "The ESF document batch is still running. You can keep working and return to this page to check progress.",
          life: 5000,
        });
      }
    } catch (error: any) {
      console.error("Unable to start ESF facility document batch", error);
      hasTriggeredFacilityDocumentBatchRef.current = false;
      setProcessingFacilityDocumentBatch(false);
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to start the ESF document batch right now.",
        life: 5000,
      });
    }
  }, [backendAPIStore, canAutoGenerateEsfDocumentsFromAssessment, homeId, isEsfDocumentProfile, refreshLatestResidentJobs, residentId]);

  const openFacilityDocumentBatchDialog = () => {
    setShowFacilityDocumentBatchDialog(true);
    if (processingFacilityDocumentBatch || facilityDocumentBatchJobDetails) {
      return;
    }
    void maybeStartEsfFacilityDocumentBatch();
  };

  const retryFacilityDocumentBatch = () => {
    if (processingFacilityDocumentBatch) {
      return;
    }
    hasTriggeredFacilityDocumentBatchRef.current = false;
    setFacilityDocumentBatchJobDetails(null);
    setFacilityDocumentBatchProcessingTime(0);
    void maybeStartEsfFacilityDocumentBatch();
  };

  useEffect(() => {
    if (!ncpJobCompletedSuccessfully) {
      hasTriggeredFacilityDocumentBatchRef.current = false;
      return;
    }
    if (!canAutoGenerateEsfDocumentsFromAssessment || !residentId || !homeId) {
      return;
    }
    if (processingFacilityDocumentBatch || facilityDocumentBatchJobDetails || hasTriggeredFacilityDocumentBatchRef.current) {
      return;
    }
    void maybeStartEsfFacilityDocumentBatch({ requireAutoEligibility: true });
  }, [
    canAutoGenerateEsfDocumentsFromAssessment,
    facilityDocumentBatchJobDetails,
    homeId,
    maybeStartEsfFacilityDocumentBatch,
    ncpJobCompletedSuccessfully,
    processingFacilityDocumentBatch,
    residentId,
  ]);

  const renderFacilityDocumentBatchStatus = () => {
    if (!(processingFacilityDocumentBatch || facilityDocumentBatchJobDetails)) {
      return null;
    }

    return (
      <div
        style={{
          marginTop: "1rem",
          border: "1px solid #dbe7f3",
          borderRadius: "18px",
          padding: "1rem 1.1rem",
          background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
        }}
      >
        <div className="flex flex-column gap-3">
          <div className="flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Creating ESF Documents</div>
              <div style={{ ...appMutedTextStyle, marginTop: "0.25rem" }}>
                Documents run one at a time in the configured facility order.
              </div>
            </div>
            <div className="flex flex-column align-items-end gap-1">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  borderRadius: "999px",
                  padding: "0.35rem 0.75rem",
                  background: "#fff7ed",
                  color: "#b45309",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                }}
              >
                <i className="pi pi-clock" />
                {String(facilityDocumentBatchJobDetails?.jobSubStatus || "").trim() || "Queued"}
              </span>
              <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                {formatEstimatedSeconds(Number(facilityDocumentBatchJobDetails?.estimatedRemainingSeconds || 0))}
              </span>
            </div>
          </div>

          {processingFacilityDocumentBatch ? (
            <div className="flex align-items-center gap-2" style={appMutedTextStyle}>
              <ProgressSpinner style={{ width: "1.4rem", height: "1.4rem" }} strokeWidth="6" />
              <span>{facilityDocumentBatchProcessingTime} seconds elapsed</span>
            </div>
          ) : null}

          <div style={{ fontWeight: 600, color: "#0f172a" }}>
            {Number(facilityDocumentBatchJobDetails?.completedDocuments || 0)} of {Number(facilityDocumentBatchJobDetails?.totalDocuments || 0)} documents completed
          </div>

          <div className="flex flex-column gap-2">
            {(Array.isArray(facilityDocumentBatchJobDetails?.documentSteps) ? facilityDocumentBatchJobDetails.documentSteps : []).map((step: any, index: number) => {
              const normalizedStatus = String(step?.status || "").trim().toUpperCase();
              const isCompleted = normalizedStatus === JOB_STATUS.COMPLETED;
              const isFailed = normalizedStatus === JOB_STATUS.FAILED;
              const isInProgress = normalizedStatus === JOB_STATUS.IN_PROGRESS || normalizedStatus === "PROCESSING";
              return (
                <div
                  key={`${step?.documentType || "document"}-${index}`}
                  className="flex flex-wrap align-items-center justify-content-between gap-2"
                  style={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    padding: "0.75rem 0.9rem",
                    background: "#fff",
                  }}
                >
                  <div className="flex align-items-center gap-2">
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "1.9rem",
                        height: "1.9rem",
                        borderRadius: "999px",
                        background: isCompleted ? "#dcfce7" : isFailed ? "#fee2e2" : isInProgress ? "#dbeafe" : "#f1f5f9",
                        color: isCompleted ? "#047857" : isFailed ? "#b91c1c" : isInProgress ? "#1d4ed8" : "#475569",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      {isCompleted ? <i className="pi pi-check" /> : isFailed ? <i className="pi pi-times" /> : index + 1}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, color: "#0f172a" }}>{step?.title || "Facility Document"}</div>
                      {step?.errorMessage ? (
                        <div style={{ color: "#b91c1c", fontSize: "0.82rem", marginTop: "0.15rem" }}>{step.errorMessage}</div>
                      ) : null}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: isCompleted ? "#047857" : isFailed ? "#b91c1c" : isInProgress ? "#1d4ed8" : "#64748b",
                    }}
                  >
                    {normalizedStatus || "PENDING"}
                  </div>
                </div>
              );
            })}
          </div>

          {(facilityDocumentBatchCompletedSuccessfully || facilityDocumentBatchFailed) ? (
            <div
              style={{
                background: facilityDocumentBatchCompletedSuccessfully ? "#ecfdf3" : "#fef2f2",
                border: `1px solid ${facilityDocumentBatchCompletedSuccessfully ? "#a7f3d0" : "#fecaca"}`,
                borderRadius: "12px",
                padding: "0.85rem 1rem",
                color: facilityDocumentBatchCompletedSuccessfully ? "#047857" : "#b91c1c",
                fontWeight: 600,
              }}
            >
              <div className="flex flex-wrap align-items-center justify-content-between gap-3">
                <span>
                  {facilityDocumentBatchCompletedSuccessfully
                    ? "The ESF document batch finished. Use Select to download, add to binder, or edit each document."
                    : "The ESF document batch finished with at least one issue. Review the document statuses and rerun any needed items."}
                </span>
                {facilityDocumentBatchFailed ? (
                  <Button
                    label="Retry Batch"
                    severity="danger"
                    outlined
                    rounded
                    onClick={retryFacilityDocumentBatch}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const normalizedHelpMessage = helpMessage.replace(/\\n/g, "\n");

  const prevCallback = () => {
    setActiveStep(prev => Math.max(0, (prev ?? 1) - 1));
  };

  const nextCallback = () => {
    setActiveStep(prev => Math.min(10, (prev ?? -1) + 1));
  };

  const openResidentNcpImporter = () => {
    navigate(residentNcpPath, {
      state: { openAssessmentToNcp: true },
    });
  };

  const scrollToCarePlanSections = () => {
    const target = document.getElementById("manage-care-plan-sections");
    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const showDSHSPopUp = () => {
    // If last NCP was successful within last 11 months, show the message
    if (ncpGeneratedSuccessfully && lastNCPGenerationDate && withinLast11Months(lastNCPGenerationDate)) {
      confirmDialog({
        message: renderConfirmMessage(
          "Assessment validity check",
          `The last assessment was completed on ${new Date(lastAssessmentDate).toLocaleDateString()}.\n\nThe last NCP was generated on ${new Date(lastNCPGenerationDate).toLocaleDateString()}.\n\nIf the resident's condition has changed significantly, upload a new assessment before generating another care plan draft.`,
        ),
        icon: 'pi pi-exclamation-triangle',
        ...appConfirmDialogClasses,
        accept: () => {
          setShowAIGeneratedNCPHelpDialog(true);
        },
        reject: () => {
          // do nothing
        }
      });
    } else {
      setShowAIGeneratedNCPHelpDialog(true);
    }
  }

  const onCompareAssessment = async (useExistingCredit: boolean = false) => {
    if (!doesNCPExtractedTextExist || !doesNextNCPExtractedTextExist) {
      toast.current?.show({ severity: 'info', summary: 'Comparison not available', detail: 'Comparison is only available when both current and next assessment data exist.', life: 5000 });
      return;
    }

    setShowCompareAssessmentPopUp(true);

    try {
      await backendAPIStore.init();

      if (!doesCompareAssessmentSummaryExist) {
        await backendAPIStore.compareAssessmentSummary(residentId!, useExistingCredit);
      } else {
        const assessmentSummaryResponse = await backendAPIStore.retrieveCompareAssessmentSummary(residentId!);
        const compareSummaryResponse = await fetch(assessmentSummaryResponse.preSignedUrl!);
        const compareSummaryData = await compareSummaryResponse.json();
        setCompareAssessmentSummaryData(compareSummaryData.response);
        return;
      }

      const PROCESSING_STATUS = 'processing';
      const COMPLETED_STATUS = 'completed';
      const FAILED_STATUS = 'failed';
      const SUCCESS_STATUS = 'success';
      let statusCheck = PROCESSING_STATUS;
      do {
        await new Promise(resolve => setTimeout(resolve, 5000));
        const response = await backendAPIStore.retrieveCompareAssessmentSummary(residentId!);
        if (response.status === COMPLETED_STATUS) {
          const summaryResponse = await fetch(response.preSignedUrl!);
          const summaryData = await summaryResponse.json();
          if (summaryData && summaryData.status === SUCCESS_STATUS) {
            setCompareAssessmentSummaryData(summaryData.response);
            statusCheck = SUCCESS_STATUS;
            setDoesCompareAssessmentSummaryExist(true);
          } else {
            toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Compare assessment summaries generation failed. Please try again.', life: 5000 });
            statusCheck = FAILED_STATUS;
          }
        } else {
          setProcessingCompareAssessmentSummary(true);
        }
      } while (statusCheck === PROCESSING_STATUS);
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        setShowCompareAssessmentPopUp(false);
        promptForCompareFeatureCredit(error, () => {
          void 0;
        });
      } else {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
      }
    }
  }

  const generateDocumentSection = () => {
    if (!doesCarePlanExist) {
      return <div className="flex justify-content-center" style={{ padding: "1rem 0" }}><Button label="Upload Assessment or Care Plan" severity="warning" rounded onClick={showDSHSPopUp} /></div>;
    }
    const canUseAssessmentActions = isAssessmentReadyForCarePlan;
    const canCompareAssessments = isAssessmentReadyForCarePlan && doesNextNCPExtractedTextExist;
    const canShowCarePlanDocumentActions = isAssessmentReadyForCarePlan || doesCarePlanExist;
    const configuredDocumentActions: CarePlanWorkflowActionItem[] = configuredDocuments.map((documentConfig) => {
      const isAssessmentGeneratedDocument = documentConfig.documentType === "negotiated_care_plan";
      const isBehaviorDocument = documentConfig.documentType === "behavior_interventions_care_plan";
      const isPreliminaryDocument = documentConfig.documentType === "preliminary_service_care_plan";

      return {
        documentConfig,
        icon: isAssessmentGeneratedDocument
          ? "pi pi-file-edit"
          : isBehaviorDocument
            ? "pi pi-book"
            : isPreliminaryDocument
              ? "pi pi-clipboard"
              : "pi pi-file",
        label: documentConfig.title,
        buttonLabel: isEsfDocumentProfile ? "Upload Existing Document and Generate" : "Download",
        severity: isAssessmentGeneratedDocument ? "warning" as const : "secondary" as const,
        disabled: !!documentConfig.requiresCarePlanDraft && !doesCarePlanExist,
        loading: getDocumentLoadingState(documentConfig.documentType),
        canEditDraft: !!documentConfig.draftSchemaKey,
        action: isAssessmentGeneratedDocument
          ? showGenerateNCPDocumentConfirmation
          : isBehaviorDocument
            ? showGenerateBehaviorInterventionsDocumentConfirmation
            : isPreliminaryDocument
              ? showGeneratePreliminaryServicePlanDocumentConfirmation
              : () => showGenerateConfiguredDocumentConfirmation(documentConfig),
        tooltip: documentConfig.description,
      };
    });

    const documentActions: CarePlanWorkflowActionItem[] = canShowCarePlanDocumentActions && isEsfDocumentProfile
      ? configuredDocumentActions.map((actionItem) => ({
        ...actionItem,
        customControls: (
          <EsfDocumentMenuButton
            menuItem={esfDocumentMenuItems.find((item) => item.label === actionItem.documentConfig?.title)}
            disabled={actionItem.disabled}
            loading={actionItem.loading || !!facilityDocumentPostAction}
          />
        ),
      }))
      : canShowCarePlanDocumentActions ? configuredDocumentActions : [];

    return (
      <div style={appNeutralPanelStyle} className="resident-careplan-actions">
        <div className="resident-careplan-actions-grid">
          <div className="resident-careplan-actions-info">
            <div className="grid mb-3">
              <div className="col-12 lg:col-8">
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Assessment and Care Plan Workflow</div>
                  <div className="mt-2" style={appMutedTextStyle}>
                    Upload or choose an assessment, then generate the care plan and related documents.
                  </div>
                </div>
                <div className="mt-3 flex flex-column gap-2" style={appMutedTextStyle}>
                  <div className="m-0">
                    Use a binder document or upload a new one.
                  </div>
                  <div className="m-0">
                    Available document actions depend on the facility profile.
                  </div>
                  {isEsfDocumentProfile ? (
                    <div className="m-0">
                      ESF forms are grouped under one Select menu, or you can create all of them in one run.
                    </div>
                  ) : null}
                  {(!isEsfCarePlanMode || isAssessmentReadyForCarePlan || doesCarePlanExist) ? (
                    <div className="mt-2">
                      <Button
                        label="Edit Care Plan Section"
                        severity="warning"
                        outlined
                        rounded
                        size="small"
                        onClick={scrollToCarePlanSections}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="col-12 lg:col-4 flex lg:justify-content-end lg:align-items-start">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    borderRadius: "999px",
                    padding: "0.5rem 0.9rem",
                    background: canUseAssessmentActions ? "#ecfdf3" : "#fff7ed",
                    color: canUseAssessmentActions ? "#047857" : "#b45309",
                    fontWeight: 700,
                  }}
                >
                  <i className={`pi ${canUseAssessmentActions ? "pi-check-circle" : "pi-clock"}`} />
                  <span>{canUseAssessmentActions ? "Assessment imported" : "Assessment is required"}</span>
                </div>
              </div>
            </div>

          </div>
          <div className="resident-careplan-action-list">
            <Tooltip target=".resident-careplan-action-tooltip-icon" position="top" />
            <ul className="list-none m-0 p-0">
              {[
                {
                  documentConfig: null,
                  icon: 'pi pi-upload',
                  label: 'Upload Assessment or Care Plan',
                  buttonLabel: 'Upload',
                  severity: 'warning' as const,
                  disabled: false,
                  loading: false,
                  canEditDraft: false,
                  action: showDSHSPopUp,
                  tooltip: ncpGeneratedSuccessfully
                    ? 'Upload a newer assessment or care plan to update the existing care plan draft.'
                    : 'Upload the resident assessment or care plan to start care plan generation.',
                },
                ...documentActions,
                ...(isUserSubscribed && ncpGeneratedSuccessfully && !isEsfDocumentProfile ? [{
                  documentConfig: null,
                  icon: 'pi pi-sitemap',
                  label: 'Import Behaviors & Interventions',
                  buttonLabel: 'Import',
                  severity: 'secondary' as const,
                  disabled: !doesCarePlanExist,
                  loading: uploadingBehaviorInterventionSources || processingBehaviorInterventionImport,
                  canEditDraft: false,
                  action: () => {
                    setBehaviorInterventionImportCompletedSuccessfully(false);
                    setBehaviorInterventionImportJobDetails(null);
                    setBehaviorInterventionImportProcessingTime(0);
                    setShowBehaviorInterventionImportDialog(true);
                  },
                  tooltip: 'Append behaviors and interventions from uploaded files or binder documents to existing care plan behaviors only.',
                }] : [])
              ].map((item: CarePlanWorkflowActionItem, i) => (
                <li
                  key={i}
                  className="flex flex-wrap align-items-center justify-content-between gap-3 py-3 px-3 resident-careplan-action-item"
                >
                  <span className={`flex align-items-center mr-2 gap-2${item.disabled ? ' text-400' : ''}`} style={{ fontWeight: 600, color: "#0f172a" }}>
                    <i className={`${item.icon} text-primary`} />
                    <span>{item.label}</span>
                    {item.tooltip ? (
                      <span
                        className="resident-careplan-action-tooltip-icon pi pi-info-circle"
                        data-pr-tooltip={item.tooltip}
                        aria-label={item.tooltip}
                      />
                    ) : null}
                  </span>
                  {item.customControls ? (
                    item.customControls
                  ) : (
                    <div className="flex align-items-center gap-2">
                      {item.canEditDraft ? (
                        <Button
                          label="Edit Sections"
                          severity="warning"
                          rounded
                          outlined
                          size="small"
                          disabled={item.disabled}
                          onClick={() => {
                            if (item.documentConfig) {
                              void openFacilityDocumentDraftDialog(item.documentConfig);
                            }
                          }}
                        />
                      ) : null}
                      <Button
                        label={item.buttonLabel}
                        severity={item.severity}
                        rounded
                        outlined
                        size="small"
                        disabled={item.disabled}
                        loading={item.loading}
                        onClick={item.action}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const runAssessmentToNcpJob = async (objectKey: string, useExistingCredit: boolean = false) => {
    try {
      setAssessmentDialogError(null);
      const startTime: number = Date.now();
      await backendAPIStore.init();
      const jobRequest = {
        referenceId: residentId,
        objectKey,
        jobType: ncpGeneratedSuccessfully ? JOB_TYPE.NEXT_ASSESSMENT_TO_NCP : JOB_TYPE.ASSESSMENT_TO_NCP,
        billingRoleType: isSharedResidentMode ? ROLE_TYPE_PROVIDER : undefined,
        useExistingCredit,
      };
      const jobDetails = await backendAPIStore.addJobDetails(jobRequest);

      setUploadingAssessment(false);
      setProcessingAssessment(true);

      let jobProgressDetails = await backendAPIStore.getJobDetails(jobDetails.jobId);
      setAssessmentJobDetails(jobProgressDetails);
      let jobStatus = jobProgressDetails['jobStatus'];
      let midTime: number = Date.now();
      do {
        await new Promise(resolve => setTimeout(resolve, 5000));
        jobProgressDetails = await backendAPIStore.getJobDetails(jobDetails.jobId);
        setAssessmentJobDetails(jobProgressDetails);
        jobStatus = jobProgressDetails['jobStatus'];
        console.log(`Current job status for assessment processing: ${jobStatus}`);
        midTime = Date.now();
        setAssessmentProcessingTime(Math.round((midTime - startTime) / 1000));
      } while (jobStatus !== JOB_STATUS.COMPLETED && jobStatus !== JOB_STATUS.FAILED && (midTime - startTime) / 1000 < 300);

      setProcessingAssessment(false);
      if (jobStatus === JOB_STATUS.COMPLETED) {
        setDoesNCPExtractedTextExist(true);
        setNcpGeneratedSuccessfully(true);
        setNcpJobCompletedSuccessfully(true);
        setShowJobSummary(true);
        setUpdateFlag((previousValue) => !previousValue);
        triggerCompletedCarePlanBinderSave(jobDetails.jobId);
        void maybeStartEsfFacilityDocumentBatch();
      } else {
        const summary = "Error";
        const detail = jobProgressDetails?.errorMessage || "There was an issue processing the assessment document. Please try again.";
        setAssessmentDialogError({ summary, detail });
        toast.current?.show({ severity: 'error', summary, detail, life: 5000 });
      }
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        showNcpAccessNeededDialog(error);
      } else {
        const toastMessage = getToastMessageForSubscriptionLimitReached(error);
        setAssessmentDialogError({
          summary: toastMessage.summary || getErrorTitle(error),
          detail: toastMessage.detail || error?.message || "There was an issue processing the assessment document. Please try again.",
        });
        toast.current?.show(toastMessage);
      }
      setProcessingAssessment(false);
      setUploadingAssessment(false);
    }
  };

  const importExistingBinderAssessment = async (useExistingCredit: boolean = false) => {
    if (!selectedBinderAssessmentDocumentId) {
      toast.current?.show({ severity: 'error', summary: 'Assessment required', detail: 'Choose an assessment from the resident binder first.', life: 5000 });
      return;
    }

    const selectedDocument = residentBinderDocuments.find((document) => document.binderDocumentId === selectedBinderAssessmentDocumentId);
    if (!selectedDocument?.binderDocumentId) {
      toast.current?.show({ severity: 'error', summary: 'Assessment not found', detail: 'The selected binder assessment could not be found.', life: 5000 });
      return;
    }

    setUploadingAssessment(true);
    setAssessmentDialogError(null);
    try {
      const latestVersion = await getLatestBinderDocumentVersion(backendAPIStore, selectedDocument.binderDocumentId);
      if (!latestVersion?.s3Key) {
        throw new Error("The selected binder assessment does not have a latest version yet.");
      }

      if (!selectedDocument.isPrimaryDocument) {
        await backendAPIStore.updateBinderDocument({
          ...selectedDocument,
          isPrimaryDocument: true,
        } as BinderDocument);
      }

      await runAssessmentToNcpJob(latestVersion.s3Key, useExistingCredit);
    } catch (error: any) {
      const summary = getErrorTitle(error);
      const detail = error?.message || "Unable to use the selected binder assessment.";
      setAssessmentDialogError({ summary, detail });
      toast.current?.show({ severity: 'error', summary, detail, life: 5000 });
      setUploadingAssessment(false);
      setProcessingAssessment(false);
    }
  };

  const uploadAssessmentFile = async (useExistingCredit: boolean = false, fileOverride?: File | null) => {
    const assessmentFile = fileOverride || selectedAssessmentFile;

    if (!assessmentFile || !residentId) {
      toast.current?.show({ severity: 'error', summary: 'No valid file selected', detail: 'Please select a valid file to upload.', life: 5000 });
      return;
    }

    setUploadingAssessment(true);
    setAssessmentDialogError(null);

    try {
      const bundle = residentBinder?.binderId
        ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
        : await ensureResidentBinderBundle(backendAPIStore, residentId);
      const assessmentFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
      if (!bundle.binder?.binderId || !assessmentFolder?.folderId) {
        throw new Error("Resident assessment binder folder is not available.");
      }

      const createdDocument = await backendAPIStore.createBinderDocument({
        binderId: bundle.binder.binderId,
        folderId: assessmentFolder.folderId,
        title: assessmentFile.name,
        documentCategory: assessmentFolder.folderName,
        isPrimaryDocument: true,
      } as BinderDocument);

      const extension = assessmentFile.name.includes(".")
        ? assessmentFile.name.split(".").pop()?.toLowerCase()
        : "";
      const objectKey = `binder/${bundle.binder.binderId}/${createdDocument.binderDocumentId}/${Date.now()}${extension ? `.${extension}` : ""}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(residentId, "resident", objectKey);
      await backendAPIStore.uploadFileToS3(presignedUrl, assessmentFile);
      await backendAPIStore.createBinderDocumentVersion({
        binderDocumentId: createdDocument.binderDocumentId,
        fileName: assessmentFile.name,
        s3Key: objectKey,
        mimeType: assessmentFile.type || "application/octet-stream",
      });

      setSelectedBinderAssessmentDocumentId(createdDocument.binderDocumentId || "");
      await loadResidentBinderData();
      await runAssessmentToNcpJob(objectKey, useExistingCredit);
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        showNcpAccessNeededDialog(error);
      } else {
        const toastMessage = getToastMessageForSubscriptionLimitReached(error);
        setAssessmentDialogError({
          summary: toastMessage.summary || getErrorTitle(error),
          detail: toastMessage.detail || error?.message || "Unable to upload and process the assessment.",
        });
        toast.current?.show(toastMessage);
      }
      setProcessingAssessment(false);
      setUploadingAssessment(false);
    }
  }

  const uploadBehaviorInterventionFilesToBinder = async (files: File[]) => {
    if (!residentId) {
      throw new Error("Resident is required before uploading behavior and intervention files.");
    }
    if (isSharedResidentMode && sharedResidentBinderAccessDenied) {
      throw new Error("Binder access is not enabled for this shared resident.");
    }
    if (!files.length) {
      return [];
    }

    const bundle = residentBinder?.binderId
      ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
      : await ensureResidentBinderBundle(backendAPIStore, residentId);
    const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
    if (!bundle.binder?.binderId || !targetFolder?.folderId) {
      throw new Error("Resident assessment binder folder is not available.");
    }

    const uploadedBinderDocumentIds: string[] = [];
    for (const sourceFile of files) {
      const createdDocument = await backendAPIStore.createBinderDocument({
        binderId: bundle.binder.binderId,
        folderId: targetFolder.folderId,
        title: sourceFile.name,
        documentCategory: targetFolder.folderName,
        isPrimaryDocument: false,
      } as BinderDocument);

      const extension = sourceFile.name.includes(".")
        ? sourceFile.name.split(".").pop()?.toLowerCase()
        : "";
      const objectKey = `binder/${bundle.binder.binderId}/${createdDocument.binderDocumentId}/${Date.now()}-${uploadedBinderDocumentIds.length}${extension ? `.${extension}` : ""}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(residentId, "resident", objectKey);
      await backendAPIStore.uploadFileToS3(presignedUrl, sourceFile);
      await backendAPIStore.createBinderDocumentVersion({
        binderDocumentId: createdDocument.binderDocumentId,
        fileName: sourceFile.name,
        s3Key: objectKey,
        mimeType: sourceFile.type || "application/octet-stream",
      });

      if (createdDocument.binderDocumentId) {
        uploadedBinderDocumentIds.push(createdDocument.binderDocumentId);
      }
    }

    return uploadedBinderDocumentIds;
  };

  const runBehaviorInterventionImportJob = async (binderDocumentIds: string[]) => {
    if (!residentId || !homeId) {
      toast.current?.show({ severity: 'error', summary: 'Resident required', detail: 'Resident information is required before starting this import.', life: 5000 });
      return;
    }

    const normalizedBinderDocumentIds = binderDocumentIds.map((value) => String(value || "").trim()).filter(Boolean);
    if (!normalizedBinderDocumentIds.length) {
      toast.current?.show({ severity: 'error', summary: 'Files required', detail: 'Choose binder files or upload one or more documents first.', life: 5000 });
      return;
    }

    try {
      const startTime: number = Date.now();
      await backendAPIStore.init();
      const jobDetails = await backendAPIStore.addJobDetails({
        referenceId: residentId,
        residentId,
        homeId,
        binderDocumentIds: normalizedBinderDocumentIds,
        jobType: JOB_TYPE.BEHAVIOR_INTERVENTION_IMPORT_TO_NCP,
        billingRoleType: isSharedResidentMode ? ROLE_TYPE_PROVIDER : undefined,
      });

      setUploadingBehaviorInterventionSources(false);
      setProcessingBehaviorInterventionImport(true);
      setBehaviorInterventionImportJobDetails(null);
      setBehaviorInterventionImportCompletedSuccessfully(false);

      let currentJobDetails = await backendAPIStore.getJobDetails(jobDetails.jobId);
      let normalizedImportSummary = normalizeBehaviorInterventionImportSummary(currentJobDetails?.behaviorInterventionImportSummary);
      setBehaviorInterventionImportJobDetails({
        ...(currentJobDetails || {}),
        behaviorInterventionImportSummary: normalizedImportSummary,
      });
      let currentStatus = currentJobDetails?.jobStatus;
      let midTime: number = Date.now();
      do {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        currentJobDetails = await backendAPIStore.getJobDetails(jobDetails.jobId);
        normalizedImportSummary = normalizeBehaviorInterventionImportSummary(currentJobDetails?.behaviorInterventionImportSummary);
        setBehaviorInterventionImportJobDetails({
          ...(currentJobDetails || {}),
          behaviorInterventionImportSummary: normalizedImportSummary,
        });
        currentStatus = currentJobDetails?.jobStatus;
        midTime = Date.now();
        setBehaviorInterventionImportProcessingTime(Math.round((midTime - startTime) / 1000));
      } while (
        currentStatus !== JOB_STATUS.COMPLETED
        && currentStatus !== JOB_STATUS.FAILED
        && (midTime - startTime) / 1000 < 300
      );

      setProcessingBehaviorInterventionImport(false);
      if (currentStatus === JOB_STATUS.COMPLETED) {
        setBehaviorInterventionImportCompletedSuccessfully(true);
        setSelectedBehaviorInterventionFiles([]);
        setSelectedBinderBehaviorDocumentIds([]);
        await loadResidentBinderData();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Import failed', detail: currentJobDetails?.errorMessage || 'There was an issue importing behavior and intervention documents. Please try again.', life: 5000 });
      }
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        showNcpAccessNeededDialog(error);
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
      setUploadingBehaviorInterventionSources(false);
      setProcessingBehaviorInterventionImport(false);
    }
  };

  const applyBehaviorInterventionImportToCarePlan = async () => {
    if (!residentId || !homeId) {
      toast.current?.show({ severity: "error", summary: "Resident required", detail: "Resident information is required before updating the care plan.", life: 5000 });
      return;
    }

    const jobId = String(behaviorInterventionImportJobDetails?.jobId || "").trim();
    if (!jobId) {
      toast.current?.show({ severity: "error", summary: "Preview required", detail: "Complete the behavior import preview before updating the care plan.", life: 5000 });
      return;
    }

    try {
      setApplyingBehaviorInterventionImport(true);
      await backendAPIStore.init();
      const selectedMatches = getSelectedBehaviorInterventionReviewMatches(
        behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary,
      ).map((match: any) => ({
        matchId: match?.matchId,
        selected: true,
        behaviorLabel: String(match?.behaviorLabel || "").trim(),
        behaviorDetails: String(match?.behaviorDetails || "").trim(),
        interventionDetails: String(match?.interventionDetails || "").trim(),
        actionToTake: String(match?.actionToTake || "").trim(),
      }));
      if (!selectedMatches.length) {
        toast.current?.show({ severity: "warn", summary: "Nothing selected", detail: "Choose at least one existing behavior row to update.", life: 5000 });
        return;
      }

      const response = await backendAPIStore.applyBehaviorInterventionImportToCarePlan({
        residentId,
        homeId,
        jobId,
        selectedMatches,
      });
      const nextSummary = normalizeBehaviorInterventionImportSummary(
        response?.summary || behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary,
      );

      setBehaviorInterventionImportJobDetails((previousValue: any) => ({
        ...(previousValue || {}),
        behaviorInterventionImportSummary: nextSummary,
      }));

      await getResidentCarePlanData();
      toast.current?.show({
        severity: "success",
        summary: "Care Plan Updated",
        detail: response?.message || "Behavior and intervention details were appended to the care plan.",
        life: 4000,
      });
      if (!getBehaviorInterventionReviewMatches(nextSummary).length) {
        setBehaviorInterventionImportCompletedSuccessfully(false);
        setShowBehaviorInterventionImportDialog(false);
        window.location.reload();
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update the care plan right now.", life: 5000 });
    } finally {
      setApplyingBehaviorInterventionImport(false);
    }
  };

  const importBehaviorInterventionFromBinder = async () => {
    if (!selectedBinderBehaviorDocumentIds.length) {
      toast.current?.show({ severity: 'error', summary: 'Files required', detail: 'Choose one or more resident binder files first.', life: 5000 });
      return;
    }

    setUploadingBehaviorInterventionSources(true);
    await runBehaviorInterventionImportJob(selectedBinderBehaviorDocumentIds);
  };

  const updateBehaviorInterventionReviewMatch = useCallback((matchId: string, updater: (match: any) => any) => {
    setBehaviorInterventionImportJobDetails((previousValue: any) => {
      const summary = normalizeBehaviorInterventionImportSummary(previousValue?.behaviorInterventionImportSummary);
      if (!summary) {
        return previousValue;
      }

      return {
        ...(previousValue || {}),
        behaviorInterventionImportSummary: {
          ...summary,
          reviewMatches: getBehaviorInterventionReviewMatches(summary).map((match: any) => {
            if (String(match?.matchId || "") !== matchId) {
              return match;
            }
            return updater(match);
          }),
        },
      };
    });
  }, []);

  const uploadBehaviorInterventionFiles = async (fileOverride?: File[]) => {
    const behaviorFiles = (fileOverride || selectedBehaviorInterventionFiles || []).filter(Boolean);
    if (!behaviorFiles.length) {
      toast.current?.show({ severity: 'error', summary: 'No valid files selected', detail: 'Please choose one or more valid files to upload.', life: 5000 });
      return;
    }

    setUploadingBehaviorInterventionSources(true);
    try {
      const uploadedBinderDocumentIds = await uploadBehaviorInterventionFilesToBinder(behaviorFiles);
      await loadResidentBinderData();
      await runBehaviorInterventionImportJob(uploadedBinderDocumentIds);
    } catch (error: any) {
      if (isSubscriptionAccessError(error)) {
        showNcpAccessNeededDialog(error);
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
      setUploadingBehaviorInterventionSources(false);
      setProcessingBehaviorInterventionImport(false);
    }
  };

  const retryAssessmentJob = async (jobId: string) => {
    setShowLastJobDetailsDialog(false);
    setShowAIGeneratedNCPHelpDialog(true);
    try {
      setAssessmentDialogError(null);
      setProcessingAssessment(true);
      await backendAPIStore.init();
      await backendAPIStore.putJobDetails(jobId, true);

      let jobProgressDetails = await backendAPIStore.getJobDetails(jobId);
      setAssessmentJobDetails(jobProgressDetails);
      let jobStatus = jobProgressDetails['jobStatus'];
      do {
        await new Promise(resolve => setTimeout(resolve, 5000));
        jobProgressDetails = await backendAPIStore.getJobDetails(jobId);
        setAssessmentJobDetails(jobProgressDetails);
        jobStatus = jobProgressDetails['jobStatus'];
        console.log(`Current job status for assessment processing: ${jobStatus}`);
      } while (jobStatus !== JOB_STATUS.COMPLETED && jobStatus !== JOB_STATUS.FAILED);

      setProcessingAssessment(false);
      if (jobStatus === JOB_STATUS.COMPLETED) {
        setDoesNCPExtractedTextExist(true);
        setNcpGeneratedSuccessfully(true);
        setNcpJobCompletedSuccessfully(true);
        setShowJobSummary(true);
        setUpdateFlag((previousValue) => !previousValue);
        triggerCompletedCarePlanBinderSave(jobId);
        void maybeStartEsfFacilityDocumentBatch();
      } else {
        const summary = "Error";
        const detail = jobProgressDetails?.errorMessage || "There was an issue processing the assessment document. Please try again.";
        setAssessmentDialogError({ summary, detail });
        toast.current?.show({ severity: 'error', summary, detail, life: 5000 });
      }
    } catch (error: any) {
      const summary = getErrorTitle(error);
      const detail = error?.message || "There was an issue retrying the assessment document.";
      setAssessmentDialogError({ summary, detail });
      toast.current?.show({ severity: 'error', summary, detail, life: 5000 });
    } finally {
      setProcessingAssessment(false);
    }
  };

  const handleFileChange = (event) => {
    setAssessmentDialogError(null);
    if (event.files && event.files[0]) {
      const file = event.files[0];
      const extension = file.name.split(".").pop()?.toLowerCase() || "";

      if (file.size > RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES || !ASSESSMENT_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) {
        toast.current?.show({ severity: 'error', summary: 'Invalid file', detail: `Please upload a PDF, DOC, or DOCX file smaller than ${RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES / (1024 * 1024)} MB.`, life: 5000 });
        setSelectedAssessmentFile(null);
        return;
      }

      setSelectedAssessmentFile(file);
    }
  };

  const handleBehaviorInterventionFileChange = (event: any) => {
    const files = Array.isArray(event?.files) ? event.files : [];
    if (!files.length) {
      setSelectedBehaviorInterventionFiles([]);
      return;
    }

    for (const file of files) {
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      if (file.size > RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES || !BEHAVIOR_IMPORT_ALLOWED_EXTENSIONS.includes(extension)) {
        toast.current?.show({
          severity: 'error',
          summary: 'Invalid file',
          detail: `Please upload PDF, DOC, DOCX, or scanned image files smaller than ${RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES / (1024 * 1024)} MB.`,
          life: 5000,
        });
        setSelectedBehaviorInterventionFiles([]);
        return;
      }
    }

    setSelectedBehaviorInterventionFiles(files);
  };

  const chooseOptions = {
    icon: 'pi pi-fw pi-images',
    label: 'Choose File',
    className: 'custom-choose-btn p-button-rounded',
    style: {
      background: "#1d4ed8",
      borderColor: "#1e40af",
      color: "#ffffff",
      fontWeight: 700,
      boxShadow: "0 12px 28px rgba(30, 64, 175, 0.22)",
    },
  };
  const uploadOptions = {
    icon: 'pi pi-fw pi-cloud-upload',
    label: 'Click To Upload',
    className: `custom-upload-btn p-button-rounded ${selectedAssessmentFile && !uploadingAssessment && !processingAssessment ? 'resident-upload-btn-pulse' : ''}`.trim(),
    style: {
      background: "#a16207",
      borderColor: "#92400e",
      color: "#ffffff",
      fontWeight: 700,
      boxShadow: "0 12px 28px rgba(146, 64, 14, 0.24)",
    },
  };
  const cancelOptions = { icon: 'pi pi-fw pi-times', className: 'custom-cancel-btn p-button-rounded p-button-outlined p-button-danger' };

  const fileHeaderTemplate = (options) => {
    const { className, chooseButton, cancelButton, uploadButton } = options;
    const chooseTourButton = chooseButton
      ? cloneElement(chooseButton as React.ReactElement<any>, {
        ...((chooseButton as React.ReactElement<any>).props || {}),
        "data-tour": "resident-upload-assessment-choose",
        onClick: (event: any) => {
          (chooseButton as React.ReactElement<any>).props?.onClick?.(event);
          if (isStepActive("choose-assessment-file")) {
            completeStep("choose-assessment-file");
          }
        },
      })
      : null;
    const uploadTourButton = uploadButton
      ? cloneElement(uploadButton as React.ReactElement<any>, {
        ...((uploadButton as React.ReactElement<any>).props || {}),
        "data-tour": "resident-upload-assessment-submit",
        onClick: (event: any) => {
          (uploadButton as React.ReactElement<any>).props?.onClick?.(event);
          if (isStepActive("upload-assessment")) {
            completeStep("upload-assessment");
          }
        },
      })
      : null;

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseTourButton}
        {!uploadingAssessment && !processingAssessment && uploadTourButton}
        {cancelButton}
      </div>
    );
  };

  const assessmentBinderFolder = getResidentBinderFolder(RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
  const assessmentBinderDocuments = getFolderDocuments(residentBinderDocuments, assessmentBinderFolder?.folderId);
  const residentBinderFoldersWithFiles = residentBinderFolders.filter((folder) => getFolderDocuments(residentBinderDocuments, folder.folderId).length > 0);
  const selectedBinderFolderDocuments = getFolderDocuments(residentBinderDocuments, selectedBinderFolderId);
  const behaviorInterventionBinderDocuments = residentBinderDocuments.filter((document: BinderDocument) => {
    if (!document?.latestVersionId) {
      return false;
    }
    const extension = String(document.title || "").split(".").pop()?.toLowerCase() || "";
    return BEHAVIOR_IMPORT_ALLOWED_EXTENSIONS.includes(extension);
  });

  return (
    <div
      style={{ width: "100%", maxWidth: "100%", display: "flex", flexDirection: "column", gap: "1.25rem", margin: "0 auto", padding: "1.25rem" }}
      className="resident-details-page m-2 p-component"
    >
      <style>
        {`
          .custom-upload-btn,
          .custom-upload-btn .p-button-label,
          .custom-upload-btn .p-button-icon {
            color: #ffffff !important;
          }

          .custom-choose-btn,
          .custom-choose-btn .p-button-label,
          .custom-choose-btn .p-button-icon {
            color: #ffffff !important;
          }

          .resident-upload-btn-pulse {
            animation: residentUploadPulse 0.95s ease-in-out infinite;
          }

          .resident-upload-btn-pulse:hover,
          .resident-upload-btn-pulse:focus {
            animation-play-state: paused;
          }

          @keyframes residentUploadPulse {
            0% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(161, 98, 7, 0.35), 0 12px 28px rgba(146, 64, 14, 0.24);
              filter: brightness(1);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 0 0 12px rgba(161, 98, 7, 0), 0 18px 34px rgba(146, 64, 14, 0.28);
              filter: brightness(1.06);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(161, 98, 7, 0), 0 12px 28px rgba(146, 64, 14, 0.24);
              filter: brightness(1);
            }
          }
        `}
      </style>
      <Toast ref={toast}></Toast>
      {fieldAutoSaveIndicator.visible ? (
        <div
          style={{
            position: "fixed",
            top: fieldAutoSaveIndicator.top,
            left: fieldAutoSaveIndicator.left,
            zIndex: 1200,
            pointerEvents: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.45rem",
            borderRadius: "999px",
            padding: "0.4rem 0.85rem",
            background: fieldAutoSaveIndicator.state === "saving" ? "#dbeafe" : "#ecfdf3",
            color: fieldAutoSaveIndicator.state === "saving" ? "#1d4ed8" : "#047857",
            border: `1px solid ${fieldAutoSaveIndicator.state === "saving" ? "#93c5fd" : "#a7f3d0"}`,
            boxShadow: "0 14px 28px rgba(15, 23, 42, 0.12)",
            fontSize: "0.88rem",
            fontWeight: 700,
          }}
        >
          <i className={`pi ${fieldAutoSaveIndicator.state === "saving" ? "pi-spin pi-spinner" : "pi-check-circle"}`} />
          <span>{fieldAutoSaveIndicator.message}</span>
        </div>
      ) : null}
      {
        loadingProfile ? (
          <div className="col-12 text-center d-flex justify-content-center align-items-center">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : residentIsArchived ? (
          <div className="col-12">
            <Dialog
              visible
              modal
              closable={false}
              closeOnEscape={false}
              dismissableMask={false}
              draggable={false}
              header={renderSurfaceHeader("Resident Archived", "Restore this resident before opening the care plan workspace.", "pi pi-archive")}
              style={mobile ? { width: "92vw" } : appDialogStyle}
              headerStyle={appDialogHeaderStyle}
              contentStyle={appDialogContentStyle}
              onHide={() => { }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div style={{ color: "#475569", lineHeight: 1.7 }}>
                  This resident is currently archived. Restore them to the active list before using the care plan, assessments, binder tools, or any related actions on this page.
                </div>
                <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412" }}>What archive means</div>
                  <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                    Archived residents stay in your records, but they are blocked from active workflows until they are restored.
                  </div>
                </div>
                {archivedResidentRestoreMessage ? (
                  <div style={{ color: "#b45309", fontWeight: 600, lineHeight: 1.6 }}>
                    {archivedResidentRestoreMessage}
                  </div>
                ) : null}
                <div className="flex flex-wrap justify-content-end gap-2">
                  <Button
                    type="button"
                    label="Back to Dashboard"
                    severity="secondary"
                    outlined
                    rounded
                    onClick={() => navigate(RouterAddress.PROVIDER_HOME)}
                  />
                  {!authStore.isAdmin() ? (
                    <Button
                      type="button"
                      label="Billing"
                      severity="secondary"
                      outlined
                      rounded
                      onClick={() => navigate(`${RouterAddress.BILLING}?tab=subscription`)}
                    />
                  ) : null}
                  <Button
                    type="button"
                    label="Restore Resident"
                    severity="warning"
                    rounded
                    loading={restoringArchivedResident || checkingArchivedResidentRestore}
                    disabled={!canRestoreArchivedResident && !authStore.isAdmin()}
                    onClick={() => { void restoreArchivedResident(); }}
                  />
                </div>
              </div>
            </Dialog>
          </div>
        ) : (
          <div className="flex flex-column gap-3">
            <div className="flex flex-wrap align-items-start justify-content-between gap-3">
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                {renderDashboardHeader(
                  residentProfileData
                    ? `${residentProfileData.firstName} ${residentProfileData.lastName} Care Plan`
                    : "Resident Care Plan",
                  isEsfCarePlanMode
                    ? "Manage resident contact details and create the related facility documents."
                    : "Upload the assessment, create the care plan draft, and continue with the related planning documents.",
                )}
              </div>
              <div className="flex justify-content-end align-items-start">
                {profileFooterTemplate()}
              </div>
            </div>

            <div className="resident-careplan-layout">
              {generateDocumentSection()}

              {/* ── Sticky section navigator ─────────────────────────── */}
              {doesCarePlanExist && !isEsfCarePlanMode && !mobile && activeStep !== null && (
                <div style={{
                  position: "sticky", top: 0, zIndex: 100,
                  background: "#ffffff",
                  borderBottom: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  padding: "0.75rem 1rem",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: "0.4rem",
                    rowGap: "0.5rem",
                    width: "100%",
                  }}>
                    {NCP_STEPS.map((step, idx) => {
                      const isActive = idx === activeStep;
                      const isDone = idx < activeStep;
                      return (
                        <div
                          key={idx}
                          onClick={() => setActiveStep(idx)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "0.35rem",
                            padding: "0.3rem 0.65rem",
                            borderRadius: "999px",
                            cursor: "pointer",
                            fontSize: "0.78rem", fontWeight: isActive ? 700 : 500,
                            background: isActive ? "#1d4ed8" : isDone ? "#fff7ed" : "#f1f5f9",
                            color: isActive ? "#fff" : isDone ? "#c2410c" : "#475569",
                            border: isActive ? "1.5px solid #1d4ed8" : isDone ? "1.5px solid #fdba74" : "1.5px solid transparent",
                            transition: "all 0.15s",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 18, height: 18, borderRadius: "50%",
                            background: isActive ? "rgba(255,255,255,0.25)" : isDone ? "#f97316" : "#94a3b8",
                            color: "#fff", fontSize: "0.6rem", fontWeight: 700, flexShrink: 0,
                          }}>
                            {isDone ? <i className="pi pi-check" style={{ fontSize: "0.55rem" }} /> : String(idx + 1)}
                          </span>
                          <i className={step.icon} style={{ fontSize: "0.75rem" }} />
                          {step.shortLabel}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {doesCarePlanExist ? <Card id="manage-care-plan-sections" style={appSectionCardStyle} data-tour="resident-careplan-sections">
                {renderSurfaceHeader(
                  "Manage Care Plan Sections",
                  !doesCarePlanExist
                    ? "Upload a resident assessment or Care Plan document to set up the profile."
                    : isEsfCarePlanMode
                    ? "Review the resident profile, points of contact, and medical allergies for coordinated service planning."
                    : "Fill in each care plan section. Changes are saved automatically.",
                  "pi pi-file-edit",
                )}
                <div className="p-3">
                  <div ref={accordionRef}>
                    <Accordion activeIndex={activeStep ?? undefined} onTabChange={(e) => setActiveStep(e.index as number | null)}>
                      {isEsfCarePlanMode ? (
                        [
                          (<AccordionTab key="esf-contact-details" header={renderAccordionHeader(0, "Resident Profile", "pi pi-user", activeStep)}>
                            <EsfSummarySection
                              sectionData={{ ...carePlanSummaryData, carePlanType: RESIDENT_CARE_PLAN_TYPE.SUMMARY }}
                              loading={loadingCarePlan}
                              onSave={(data) => submitCarePlanData(data, { showSuccessToast: true })}
                            />
                          </AccordionTab>)
                          , (<AccordionTab key="esf-responsible-parties" header={renderAccordionHeader(1, "Point of Contacts", "pi pi-users", activeStep)}>
                            <EsfResponsiblePartiesSection
                              sectionData={carePlanResponsiblePartiesData}
                              loading={loadingCarePlan}
                              onSave={(data) => submitCarePlanData(data, { showSuccessToast: true })}
                            />
                          </AccordionTab>)
                          , (<AccordionTab key="esf-medical-allergies" header={renderAccordionHeader(2, "Medical & Allergies", "pi pi-heart", activeStep)}>
                            <EsfMedicalAllergiesSection
                              sectionData={{
                                ...carePlanMedicationManagementData,
                                carePlanType: ESF_MEDICAL_ALLERGIES_CARE_PLAN_TYPE,
                                allergiesDescribed: carePlanMedicationManagementData?.allergiesDescribed || carePlanSummaryData?.residentAllergies || "",
                                medicalConditions: carePlanSummaryData?.medicalDiagnosesDetails || "",
                              }}
                              loading={loadingCarePlan}
                              onSave={(data) => submitCarePlanData(data, { showSuccessToast: true })}
                            />
                          </AccordionTab>)
                        ]
                      ) : null}
                      {!isEsfCarePlanMode ? <AccordionTab header={renderAccordionHeader(0, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.SUMMARY), "pi pi-file-edit", activeStep)}>
                        <SummarySection
                          sectionData={{
                            ...carePlanSummaryData,
                            acesId: carePlanSummaryData?.acesId || "",
                          }}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab> : null}
                      {!isEsfCarePlanMode ? [
                      (<AccordionTab key="responsible-parties" header={renderAccordionHeader(1, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES), "pi pi-users", activeStep)}>
                        <ResponsiblePartiesSection
                          sectionData={carePlanResponsiblePartiesData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="communication" header={renderAccordionHeader(2, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.COMMUNICATION), "pi pi-comments", activeStep)}>
                        <CommunicationSection
                          sectionData={carePlanCommunicationData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="medication-management" header={renderAccordionHeader(3, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT), "pi pi-heart", activeStep)}>
                        <MedicationManagementSection
                          sectionData={carePlanMedicationManagementData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="health-indicators" header={renderAccordionHeader(4, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS), "pi pi-chart-line", activeStep)}>
                        <HealthIndicatorSection sectionData={carePlanHealthIndicatorData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="body-chart" header={renderAccordionHeader(5, capitalizeFirstLetter(BODY_CHART_CARE_PLAN_TYPE), "pi pi-map", activeStep)}>
                        <BodyChartSection sectionData={carePlanBodyChartData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          contextNote="NCP - Body Chart will be included in the Preliminary Care Plan document only" />
                      </AccordionTab>),
                      (<AccordionTab key="treatment-program-therapies" header={renderAccordionHeader(6, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES), "pi pi-list-check", activeStep)}>
                        <TreatmentProgramSection sectionData={carePlanTreatmentProgramTherapiesData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="psych-social-cognitive" header={renderAccordionHeader(7, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE), "pi pi-brain", activeStep)}>
                        <PsychSocialSection sectionData={carePlanPsychSocialCognitiveData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="left-alone-status" header={renderAccordionHeader(8, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS), "pi pi-user", activeStep)}>
                        <LeftAloneSection sectionData={carePlanLeftAloneStatusData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="universal-precautions" header={renderAccordionHeader(9, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS), "pi pi-shield", activeStep)}>
                        <UniversalPrecautionSection sectionData={carePlanUniversalPrecautionsData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="activities-of-daily-living" header={renderAccordionHeader(10, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING), "pi pi-home", activeStep)}>
                        <ActivitiesOfDailyLivingSection sectionData={carePlanActivitiesOfDailyLivingData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      (<AccordionTab key="instrumental-activities-of-daily-living" header={renderAccordionHeader(11, capitalizeFirstLetter(RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING), "pi pi-wrench", activeStep)}>
                        <InstrumentalActivitiesSection sectionData={carePlanInstrumentalActivitiesData}
                          submitButtonClick={submitCarePlanData}
                          loadingCarePlan={loadingCarePlan}
                          backButtonClick={prevCallback}
                          nextButtonClick={nextCallback}
                          showHelpMessage={showHelpMessage} />
                      </AccordionTab>),
                      ] : null}
                    </Accordion>
                  </div>
                </div>
              </Card> : null}
            </div>
          </div>)
      }

      <Dialog
        visible={editProfileModalOpen}
        modal
        header={renderSurfaceHeader("Edit Profile", "Update the resident identity and profile details.", "pi pi-user-edit")}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setEditProfileModalOpen(false)}>
        <EditResidentModal isOpen={editProfileModalOpen} toggleModal={setEditProfileModalOpen}
          data={residentProfileData} flag={updateFlag} setFlag={setUpdateFlag} />
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("DSHS Guidance", "Helpful context for completing a specific care-plan field.", "pi pi-info-circle")}
        visible={showHelpDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => setShowHelpDialog(false)} closable closeOnEscape>
        <div>
          {/[<][a-z/!]/i.test(normalizedHelpMessage) ? (
            <>
              <style>{residentNcpHelpHtmlStyle}</style>
              <div
                className="resident-ncp-help-html"
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichHtml(normalizedHelpMessage).replace(/\n/g, "<br />"),
                }}
              />
            </>
          ) : (
            <p style={{ whiteSpace: 'pre-line', margin: 0, color: "#475569", lineHeight: 1.7 }}>{normalizedHelpMessage}</p>
          )}
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          "More Access Needed",
          "Choose how you want to continue.",
          "pi pi-wallet",
        )}
        visible={showFeatureAccessDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setShowFeatureAccessDialog(false);
          setShowCompareAssessmentPopUp(false);
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7, whiteSpace: "pre-line" }}>
            {featureAccessDialogMessage}
          </div>
          <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem" }}>
            <div style={{ fontWeight: 700, color: "#9a3412" }}>Options</div>
            <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
              {featureAccessDialogForSubscribedUser
                ? "You can wait until your next billing date for the included usage to refresh."
                : "Open Billing to start or manage the subscription for this feature."}
            </div>
          </div>
          <div className="flex flex-wrap justify-content-end gap-2">
            {featureAccessDialogForSubscribedUser ? (
              <Button
                label="Got It"
                severity="secondary"
                outlined
                rounded
                onClick={() => {
                  setShowFeatureAccessDialog(false);
                  setShowCompareAssessmentPopUp(false);
                }}
              />
            ) : (
              <Button
                label="View Subscription"
                severity="secondary"
                outlined
                rounded
                onClick={() => {
                  setShowFeatureAccessDialog(false);
                  setShowCompareAssessmentPopUp(false);
                  navigate(`${RouterAddress.BILLING}?tab=subscription`);
                }}
              />
            )}
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("More NCP Access Needed", undefined, "pi pi-wallet")}
        visible={showNcpAccessDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setShowNcpAccessDialog(false);
          setShowAIGeneratedNCPHelpDialog(false);
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7, whiteSpace: "pre-line" }}>
            {ncpAccessDialogMessage}
          </div>
          {!requiresProviderSubscriptionMessageOnly && !isUserSubscribed && ncpAccessDialogAllowBillingSubscription ? (
            <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem" }}>
              <div style={{ fontWeight: 700, color: "#9a3412" }}>Options</div>
              <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                Open Billing to start or manage your subscription for this feature.
              </div>
            </div>
          ) : null}
          {!requiresProviderSubscriptionMessageOnly ? (
          <div className="flex flex-wrap justify-content-end gap-2">
            {!isUserSubscribed && ncpAccessDialogAllowBillingSubscription && (
              <Button
                label="Subscription"
                severity="secondary"
                outlined
                rounded
                onClick={() => {
                  setShowNcpAccessDialog(false);
                  setShowAIGeneratedNCPHelpDialog(false);
                  navigate(`${RouterAddress.BILLING}?tab=subscription`);
                }}
              />
            )}
            {isUserSubscribed && ncpAccessDialogAvailableCredits > 0 && ncpAccessDialogAllowBillingCredits ? (
              <Button
                label="Use 1 Automation Credit"
                severity="warning"
                rounded
                onClick={() => {
                  setShowNcpAccessDialog(false);
                  if (selectedBinderAssessmentDocumentId) {
                    void importExistingBinderAssessment(true);
                  } else {
                    void uploadAssessmentFile(true);
                  }
                }}
              />
            ) : isUserSubscribed && ncpAccessDialogAllowBillingCredits ? (
              <Button
                label="Buy Automation Credits"
                severity="warning"
                rounded
                onClick={() => {
                  setShowNcpAccessDialog(false);
                  setShowAIGeneratedNCPHelpDialog(false);
                  navigate(`${RouterAddress.BILLING}?tab=credits`);
                }}
              />
            ) : null}
          </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          facilityDocumentSourceDialogConfig?.title || "Upload Existing Document and Generate",
          "",
          "pi pi-upload",
        )}
        visible={showFacilityDocumentSourceDialog}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        closable
        onHide={() => {
          setShowFacilityDocumentSourceDialog(false);
          setSelectedFacilityDocumentSourceFile(null);
          setFacilityDocumentSourceDialogError("");
        }}
      >
        <div className="flex flex-column gap-3">
          <div style={appMutedTextStyle}>
            Upload an existing assessment or supporting document to generate this ESF form from that file. If you skip upload, we will use the resident&apos;s current parsed assessment.
          </div>
          <FileUpload
            mode="advanced"
            name="facilityDocumentSourceFile"
            onSelect={handleFacilityDocumentSourceFileChange}
            accept={FACILITY_DOCUMENT_SOURCE_UPLOAD_ACCEPT}
            maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
            headerTemplate={fileHeaderTemplate}
            className="col"
            chooseOptions={chooseOptions}
            cancelOptions={cancelOptions}
            uploadOptions={{ ...uploadOptions, label: "Upload Existing Document and Generate" }}
            customUpload
            uploadHandler={() => { void submitFacilityDocumentSourceGeneration(); }}
            emptyTemplate={<p className="m-0">Upload PDF, DOC, DOCX, TXT, or scanned image files here. We will extract text from this file and use it for this document generation.</p>}
          />
          {selectedFacilityDocumentSourceFile ? (
            <div style={{ fontSize: "0.92rem", color: "#0f172a" }}>
              Selected file: <strong>{selectedFacilityDocumentSourceFile.name}</strong>
            </div>
          ) : null}
          {facilityDocumentSourceDialogError ? (
            <Message severity="error" text={facilityDocumentSourceDialogError} className="w-full" />
          ) : null}
          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              label="Generate From Current Assessment"
              severity="secondary"
              outlined
              rounded
              onClick={() => { void submitFacilityDocumentSourceGeneration(); }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          facilityDocumentJobConfig?.title || "Facility Document Generation",
          "",
          "pi pi-file-edit",
        )}
        visible={showFacilityDocumentJobDialog}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        closable
        onHide={() => {
          setShowFacilityDocumentJobDialog(false);
          setFacilityDocumentPostAction(null);
          if (
            typeof window !== "undefined"
            && String(facilityDocumentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED
          ) {
            reloadCurrentResidentNcpPage();
          }
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {!facilityDocumentJobCompletedSuccessfully && !facilityDocumentJobFailed ? (
            <div className="col-12 text-center d-flex justify-content-center align-items-center">
              <div>
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-3" style={{ fontWeight: 700, color: "#0f172a" }}>
                  {facilityDocumentJobDetails?.jobSubStatus || "Generating document"}
                </div>
                <div className="block mt-2" style={appMutedTextStyle}>
                  {facilityDocumentProcessingTime > 0
                    ? `Processing... ${facilityDocumentProcessingTime} seconds elapsed`
                    : "Starting the document job..."}
                </div>
              </div>
            </div>
          ) : facilityDocumentJobFailed ? (
            <div style={{ color: "#b91c1c", lineHeight: 1.6 }}>
              {facilityDocumentJobDetails?.errorMessage || facilityDocumentJobDetails?.jobSubStatus || "Document generation failed."}
            </div>
          ) : (
            <>
              <div
                style={{
                  border: "1px solid #dbe7f3",
                  borderRadius: "14px",
                  background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                  padding: "1rem",
                }}
              >
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>
                  Document ready
                </div>
                <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                  {facilityDocumentJobConfig?.title || "This document"} finished generating
                  {formatFacilityDocumentGeneratedAt(facilityDocumentJobDetails?.completedAt || facilityDocumentJobDetails?.createdAt)
                    ? ` on ${formatFacilityDocumentGeneratedAt(facilityDocumentJobDetails?.completedAt || facilityDocumentJobDetails?.createdAt)}.`
                    : "."}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-content-end">
                <Button
                  label="Download File"
                  severity="secondary"
                  outlined
                  rounded
                  loading={facilityDocumentPostAction === "download"}
                  onClick={() => {
                    void handleCompletedFacilityDocumentAction("download", undefined, undefined, { reloadAfter: true });
                  }}
                />
                <Button
                  label="Add to Binder"
                  severity="warning"
                  rounded
                  disabled={!canUseSharedResidentBinder}
                  loading={facilityDocumentPostAction === "binder"}
                  onClick={() => {
                    void handleCompletedFacilityDocumentAction("binder", undefined, undefined, { reloadAfter: true });
                  }}
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(profileSetupMode ? "Set-up Profile" : "Assessment to Care Plan Automation", "", "pi pi-sparkles")}
        visible={showAIGeneratedNCPHelpDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        closable
        onHide={() => {
          setShowAIGeneratedNCPHelpDialog(false);
          setAssessmentDialogError(null);
          if (
            typeof window !== "undefined"
            && String(assessmentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED
          ) {
            window.location.reload();
          }
        }} closeOnEscape footer={
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
            {AI_MESSAGE_FOR_COMPLIANCE}
          </p>
        }>
        <div>
          {assessmentDialogError ? (
            <div className="mb-3">
              <Message
                severity="error"
                text={`${assessmentDialogError.summary}: ${assessmentDialogError.detail}`}
                className="w-full"
              />
            </div>
          ) : null}
          {
            !processingAssessment && !assessmentJobDetails && (
              <>
                <div
                  data-tour="resident-upload-assessment"
                  style={{
                    border: "1px solid #dbe7f3",
                    borderRadius: "14px",
                    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                    padding: "0.9rem 1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.25rem" }}>
                    {profileSetupMode ? "Upload a resident assessment or Care Plan document" : "Start with an assessment or NCP"}
                  </div>
                  <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                    {residentBinderFoldersWithFiles.length
                      ? (profileSetupMode ? "Choose a resident assessment or care plan document from the binder, or upload one below to set up the profile." : "Choose an assessment or NCP already in the resident binder, or upload a new one below to generate the care plan draft.")
                      : (profileSetupMode ? "Upload a resident assessment or care plan document below to set up the profile. It will be saved to the resident binder automatically." : "Upload a new assessment or NCP below to generate the care plan draft. It will be saved to the resident binder automatically.")}
                  </div>
                </div>
                {residentBinderFoldersWithFiles.length ? (
                  <>
                    <div className="flex flex-column gap-3 mb-4">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Use an assessment or NCP already in the resident binder</div>
                      <div className="grid">
                        <div className="col-12 md:col-6 flex flex-column gap-2">
                          <label htmlFor="resident-ncp-binder-folder" className="font-semibold text-900">Binder folder</label>
                          <Dropdown
                        inputId="resident-ncp-binder-folder"
                        value={selectedBinderFolderId}
                        options={residentBinderFoldersWithFiles}
                        optionLabel="folderName"
                        optionValue="folderId"
                        onChange={(e) => {
                          const folderId = String(e.value || "");
                          const files = getFolderDocuments(residentBinderDocuments, folderId);
                          setSelectedBinderFolderId(folderId);
                          setSelectedBinderAssessmentDocumentId(files.length === 1 ? files[0].binderDocumentId || "" : "");
                        }}
                        placeholder="Choose a Binder folder"
                        className="w-full"
                            emptyMessage="No Binder folders found."
                          />
                        </div>
                        <div className="col-12 md:col-6 flex flex-column gap-2">
                          <label htmlFor="resident-ncp-binder-file" className="font-semibold text-900">Binder file</label>
                          <Dropdown
                        inputId="resident-ncp-binder-file"
                        value={selectedBinderAssessmentDocumentId}
                        options={selectedBinderFolderDocuments}
                        optionLabel="title"
                        optionValue="binderDocumentId"
                        onChange={(e) => setSelectedBinderAssessmentDocumentId(e.value)}
                        placeholder="Choose a file from the selected folder"
                        className="w-full"
                        itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
                        valueTemplate={(option: BinderDocument | null) =>
                          option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose a file from the selected folder</span>
                        }
                            emptyMessage="No files found in the selected folder."
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap align-items-center gap-2">
                        <Button
                          label="Use Binder File"
                          severity="warning"
                          outlined
                          rounded
                          disabled={!selectedBinderAssessmentDocumentId || uploadingAssessment || processingAssessment}
                          onClick={() => { void importExistingBinderAssessment(); }}
                        />
                      </div>
                    </div>
                    <Divider align="center">or upload a new assessment or care plan document to the binder</Divider>
                  </>
                ) : null}
                <FileUpload mode="advanced" name="profileImage" onSelect={handleFileChange} accept={ASSESSMENT_UPLOAD_ACCEPT} maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
                  headerTemplate={fileHeaderTemplate} className="col"
                  chooseOptions={chooseOptions} cancelOptions={cancelOptions} uploadOptions={uploadOptions}
                  customUpload uploadHandler={(event: any) => { void uploadAssessmentFile(false, event?.files?.[0] || null); }}
                  emptyTemplate={<p className="m-0">Upload or drag and drop a PDF, DOC, or DOCX assessment or care plan document here. It will be saved into the resident binder and used for this import.</p>}
                />
                {
                  doesCarePlanExist && !uploadingAssessment && (
                    <p>
                      <strong>Note: </strong>
                      <i>Current NCP section data will be overwritten after assessment processing.
                        Download or save a copy to the binder first if you need a backup.</i>
                    </p>
                  )
                }
              </>)
          }
          {
            (uploadingAssessment || processingAssessment) && (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-2">
                  {uploadingAssessment
                    ? "Uploading assessment..."
                    : `Processing... ${assessmentProcessingTime} seconds elapsed`}
                </div>
              </div>
            )
          }
          <div>
            {
              assessmentJobDetails && (
                <div className="col-12 text-center d-flex justify-content-center align-items-center">
                  {
                    processingAssessment && (
                      <div className="block mt-2"><strong>Job Status: </strong>{String(assessmentJobDetails['jobSubStatus'] || "").replace(/\bNCP\b/g, "Care Plan")}</div>
                    )
                  }
                  {
                    assessmentJobDetails['ncpSections'] && (
                      <>
                        <h3 className="mt-4">Import Summary: {assessmentJobDetails['jobStatus']}</h3>
                        {(assessmentJobDetails['ncpSections'] || []).some((section) => !section.success) && (
                          <>
                            <div style={{ fontWeight: 600, color: "#b91c1c", marginBottom: "0.4rem", fontSize: "0.9rem" }}>
                              Failed Sections:
                            </div>
                            <ul style={{ listStyleType: 'none', paddingLeft: 0 }} className="text-left">
                              {(assessmentJobDetails['ncpSections'] || []).filter((section) => !section.success).map((section) => (
                                <li key={section.carePlanType} className="mb-2">
                                  <span style={{ color: "#b91c1c" }}>
                                    <i className="pi pi-times-circle" style={{ marginRight: "0.4rem" }} />
                                    <strong style={{ textTransform: "capitalize" }}>{section.carePlanType}</strong>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {
                          (assessmentJobDetails['ncpSections'] || []).some(section => !section.success) && (
                            <div className="m-2">
                              <Button label="Retry Failed Items" severity="warning" rounded outlined onClick={() => retryAssessmentJob(assessmentJobDetails['jobId'])} />
                            </div>
                          )
                        }
                      </>
                    )
                  }
                  {
                    ncpJobCompletedSuccessfully && !processingAssessment && (
                      <div className="m-4">
                        <div style={{
                          background: "#fef3c7", border: "1.5px solid #fcd34d",
                          borderRadius: "10px", padding: "0.9rem 1.1rem",
                          display: "flex", alignItems: "flex-start", gap: "0.7rem",
                          marginBottom: "1rem",
                        }}>
                          <i className="pi pi-exclamation-triangle" style={{ color: "#d97706", fontSize: "1.3rem", marginTop: "0.1rem", flexShrink: 0 }} />
                          <div>
                            <div style={{ fontWeight: 700, color: "#92400e", fontSize: "0.95rem", marginBottom: "0.3rem" }}>
                              Care Plan Draft Ready
                            </div>
                            <div style={{ color: "#78350f", fontSize: "0.88rem", lineHeight: 1.6 }}>
                              Review the care plan, add resident-specific caregiver interventions, and verify the clinical details before using it as final.
                            </div>
                          </div>
                        </div>

                        <div style={{ fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
                        </div>
                      </div>)
                  }
                  {canAutoGenerateEsfDocumentsFromAssessment ? renderFacilityDocumentBatchStatus() : null}
                </div>
              )
            }
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Create Facility Documents", "Run all facility document jobs one at a time.", "pi pi-file")}
        visible={showFacilityDocumentBatchDialog}
        style={mobile ? { width: "94vw" } : { ...appDialogStyle, width: "78vw", maxWidth: "980px" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          if (!processingFacilityDocumentBatch) {
            setShowFacilityDocumentBatchDialog(false);
            if (
              typeof window !== "undefined"
              && String(facilityDocumentBatchJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED
            ) {
              reloadCurrentResidentNcpPage();
            }
          }
        }}
        closeOnEscape={!processingFacilityDocumentBatch}
        footer={(
          <div className="flex justify-content-end gap-2 w-full">
            <Button
              label="Close"
              severity="secondary"
              outlined
              rounded
              disabled={processingFacilityDocumentBatch}
              onClick={() => {
                setShowFacilityDocumentBatchDialog(false);
                if (
                  typeof window !== "undefined"
                  && String(facilityDocumentBatchJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED
                ) {
                  reloadCurrentResidentNcpPage();
                }
              }}
            />
          </div>
        )}
      >
        {renderFacilityDocumentBatchStatus() || (
          <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>
              Ready to create all facility documents
            </div>
            <div style={appMutedTextStyle}>
              This will run each facility document job in sequence, not in parallel, using the latest assessment and saved section edits.
            </div>
            <div className="mt-3">
              <Button
                label="Start"
                severity="warning"
                rounded
                icon="pi pi-play"
                onClick={openFacilityDocumentBatchDialog}
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          activeFacilityDocumentDraft?.title || activeFacilityDocumentDraftConfig?.title || "Edit Document Sections",
          "Review and update the template sections before generating the final document.",
          "pi pi-file-edit",
        )}
        visible={showFacilityDocumentDraftDialog}
        style={mobile ? { width: "94vw" } : { ...appDialogStyle, width: "82vw", maxWidth: "1200px" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          if (!savingFacilityDocumentDraft) {
            setShowFacilityDocumentDraftDialog(false);
          }
        }}
        closeOnEscape={!savingFacilityDocumentDraft}
        footer={(
          <div className="flex justify-content-end gap-2 w-full">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              disabled={savingFacilityDocumentDraft}
              onClick={() => setShowFacilityDocumentDraftDialog(false)}
            />
            <Button
              label="Save Sections"
              severity="warning"
              rounded
              loading={savingFacilityDocumentDraft}
              onClick={() => { void saveFacilityDocumentDraft(); }}
            />
          </div>
        )}
      >
        {loadingFacilityDocumentDraft ? (
          <div className="col-12 text-center d-flex justify-content-center align-items-center">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : activeFacilityDocumentDraft ? (
          <div className="flex flex-column gap-3">
            <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>
                {activeFacilityDocumentDraft.title}
              </div>
              <div style={appMutedTextStyle}>{activeFacilityDocumentDraft.description}</div>
            </div>
            {(activeFacilityDocumentDraft.sections || []).map((section, sectionIndex) => (
              <Card key={section.sectionKey} style={appSectionCardStyle}>
                <div className="flex flex-column gap-3">
                  <div
                    className="flex flex-column gap-2"
                    style={{
                      padding: "0.9rem 1rem",
                      borderRadius: "16px",
                      background: "linear-gradient(180deg, #fffaf0 0%, #fff 100%)",
                      border: "1px solid #f6d6a8",
                    }}
                  >
                    <div className="flex align-items-center gap-2 flex-wrap">
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "1.8rem",
                          height: "1.8rem",
                          borderRadius: "999px",
                          background: "#b45309",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                        }}
                      >
                        {sectionIndex + 1}
                      </span>
                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>{section.title}</div>
                    </div>
                    {section.description ? (
                      <div style={appMutedTextStyle}>{section.description}</div>
                    ) : null}
                  </div>
                  {section.sectionKey === CHALLENGING_BEHAVIORS_SECTION_KEY ? (() => {
                    const sectionFields = section.fields || [];
                    const challengingRows = extractChallengingBehaviorRows(sectionFields);
                    const progressionField = sectionFields.find((field) => field.fieldKey === "behavior_progression_response");
                    const behaviorFieldTemplate = sectionFields.find((field) => /^behavior_selection_\d+$/.test(field.fieldKey));
                    const interventionFieldTemplate = sectionFields.find((field) => /^intervention_selection_\d+$/.test(field.fieldKey));

                    return (
                      <div className="flex flex-column gap-3">
                        <div
                          style={{
                            border: "1px solid #dbe4f0",
                            borderRadius: "16px",
                            overflow: "hidden",
                            background: "#fff",
                          }}
                        >
                          <div
                            className="grid"
                            style={{
                              margin: 0,
                              background: "#f8fafc",
                              borderBottom: "1px solid #dbe4f0",
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            <div className="col-12 md:col-3" style={{ padding: "0.85rem 1rem" }}>Behavior Name</div>
                            <div className="col-12 md:col-5" style={{ padding: "0.85rem 1rem", borderLeft: "1px solid #dbe4f0" }}>Details</div>
                            <div className="col-12 md:col-3" style={{ padding: "0.85rem 1rem", borderLeft: "1px solid #dbe4f0" }}>Interventions</div>
                            <div className="col-12 md:col-1" style={{ padding: "0.85rem 1rem", borderLeft: "1px solid #dbe4f0", textAlign: "center" }}>Actions</div>
                          </div>
                          {challengingRows.map((row, rowIndex) => (
                            <div
                              key={`${section.sectionKey}-row-${row.rowId}`}
                              className="grid"
                              style={{
                                margin: 0,
                                borderBottom: rowIndex === challengingRows.length - 1 ? "none" : "1px solid #e2e8f0",
                                background: rowIndex % 2 === 0 ? "#fff" : "#fcfdff",
                              }}
                            >
                              <div className="col-12 md:col-3" style={{ padding: "0.85rem 1rem" }}>
                                <Dropdown
                                  value={row.behaviorKey || ""}
                                  options={behaviorFieldTemplate?.options || []}
                                  optionLabel="label"
                                  optionValue="value"
                                  onChange={(e) => updateChallengingBehaviorDraftRow(section.sectionKey, row.rowId, "behaviorKey", String(e.value || ""))}
                                  className="w-full"
                                  placeholder={behaviorFieldTemplate?.placeholder || "Select behavior"}
                                />
                              </div>
                              <div className="col-12 md:col-5" style={{ padding: "0.85rem 1rem", borderLeft: "1px solid #eef2f7" }}>
                                <InputTextarea
                                  value={row.behaviorDetails || ""}
                                  onChange={(e) => updateChallengingBehaviorDraftRow(section.sectionKey, row.rowId, "behaviorDetails", e.target.value || "")}
                                  rows={4}
                                  autoResize
                                  className="w-full"
                                  placeholder="Add behavior-specific details, examples, frequency, or context."
                                />
                              </div>
                              <div className="col-12 md:col-3" style={{ padding: "0.85rem 1rem", borderLeft: "1px solid #eef2f7" }}>
                                <MultiSelect
                                  value={row.interventionKeys || []}
                                  options={interventionFieldTemplate?.options || []}
                                  optionLabel="label"
                                  optionValue="value"
                                  onChange={(e) => updateChallengingBehaviorDraftRow(
                                    section.sectionKey,
                                    row.rowId,
                                    "interventionKeys",
                                    Array.isArray(e.value) ? e.value.map((value) => String(value || "").trim()).filter(Boolean) : [],
                                  )}
                                  className="w-full"
                                  display="chip"
                                  maxSelectedLabels={2}
                                  filter
                                  placeholder={interventionFieldTemplate?.placeholder || "Select intervention"}
                                />
                              </div>
                              <div className="col-12 md:col-1 flex justify-content-center align-items-start" style={{ padding: "0.85rem 0.6rem", borderLeft: "1px solid #eef2f7" }}>
                                <Button
                                  icon="pi pi-trash"
                                  severity="secondary"
                                  text
                                  rounded
                                  aria-label={`Remove behavior row ${rowIndex + 1}`}
                                  onClick={() => removeChallengingBehaviorDraftRow(section.sectionKey, row.rowId)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
                          <div style={appMutedTextStyle}>
                            Add one row per behavior. Pick from the approved CBHS behavior and intervention lists.
                          </div>
                          <Button
                            label="Add New Entry"
                            icon="pi pi-plus"
                            severity="warning"
                            rounded
                            onClick={() => addChallengingBehaviorDraftRow(section.sectionKey)}
                          />
                        </div>
                        {progressionField ? (
                          <div
                            className="flex flex-column"
                            style={{
                              border: "1px solid #dbe4f0",
                              borderRadius: "14px",
                              background: "#fff",
                              padding: "0.95rem 1rem",
                              gap: "0.6rem",
                            }}
                          >
                            <div>
                              <label
                                htmlFor={`${section.sectionKey}-${progressionField.fieldKey}`}
                                style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.2rem" }}
                              >
                                {progressionField.label}
                              </label>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                Use this for links, escalation patterns, or sequence across behaviors.
                              </div>
                            </div>
                            <InputTextarea
                              id={`${section.sectionKey}-${progressionField.fieldKey}`}
                              value={progressionField.value || ""}
                              onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, progressionField.fieldKey, e.target.value || "")}
                              rows={6}
                              autoResize
                              className="w-full"
                            />
                          </div>
                        ) : null}
                      </div>
                    );
                  })() : (
                    <div className="grid">
                      {(section.fields || []).map((field) => {
                        const { inputType, options } = inferFacilityDocumentDraftFieldPresentation(field);
                        const isLongField = inputType === "textarea";
                        const fieldId = `${section.sectionKey}-${field.fieldKey}`;
                        const helpText = inputType === "textarea"
                          ? "Use full notes, bullets, and line breaks where needed."
                          : inputType === "date"
                            ? "Pick the date from the calendar."
                            : inputType === "checkbox"
                              ? "Turn this on only when it applies."
                              : inputType === "radio" || inputType === "dropdown"
                                ? "Choose the option that best matches the document."
                                : "Keep this field concise and document-ready.";
                        return (
                          <div
                            key={fieldId}
                            className={
                              isLongField
                                ? "col-12"
                                : inputType === "radio"
                                  ? "col-12 xl:col-8"
                                  : "col-12 md:col-6"
                            }
                          >
                            <div
                              className="flex flex-column h-full"
                              style={{
                                border: "1px solid #dbe4f0",
                                borderRadius: "14px",
                                background: "#fff",
                                padding: "0.95rem 1rem",
                                gap: "0.6rem",
                                minWidth: 0,
                              }}
                            >
                              <div>
                                <label
                                  htmlFor={fieldId}
                                  style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.2rem" }}
                                >
                                  {field.label}
                                </label>
                                <div style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                  {helpText}
                                </div>
                              </div>
                              {inputType === "textarea" ? (
                                <InputTextarea
                                  id={fieldId}
                                  value={field.value || ""}
                                  onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, field.fieldKey, e.target.value || "")}
                                  rows={field.value && field.value.length > 500 ? 10 : 6}
                                  autoResize
                                  className="w-full"
                                />
                              ) : inputType === "date" ? (
                                <Calendar
                                  inputId={fieldId}
                                  value={parseFacilityDocumentDraftDate(field.value || "")}
                                  onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, field.fieldKey, formatFacilityDocumentDraftDate((e.value as Date) || null))}
                                  showIcon
                                  dateFormat="mm/dd/yy"
                                  className="w-full"
                                  inputClassName="w-full"
                                  placeholder="Select date"
                                />
                              ) : inputType === "checkbox" ? (
                                <div className="flex align-items-center gap-2 flex-wrap" style={{ minHeight: "2.75rem" }}>
                                  <Checkbox
                                    inputId={fieldId}
                                    checked={String(field.value || "").trim().toLowerCase() === "true"}
                                    onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, field.fieldKey, e.checked ? "true" : "false")}
                                  />
                                  <label htmlFor={fieldId} style={{ color: "#0f172a", margin: 0 }}>
                                    Mark as selected
                                  </label>
                                </div>
                              ) : inputType === "radio" ? (
                                <div className="flex flex-wrap gap-3" style={{ minHeight: "2.75rem" }}>
                                  {options.map((option) => (
                                    <div key={`${fieldId}-${option.value}`} className="flex align-items-center gap-2">
                                      <RadioButton
                                        inputId={`${fieldId}-${option.value}`}
                                        name={fieldId}
                                        value={option.value}
                                        checked={String(field.value || "") === option.value}
                                        onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, field.fieldKey, String(e.value || ""))}
                                      />
                                      <label htmlFor={`${fieldId}-${option.value}`} style={{ color: "#0f172a", margin: 0 }}>
                                        {option.label}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              ) : inputType === "dropdown" ? (
                                <Dropdown
                                  inputId={fieldId}
                                  value={field.value || ""}
                                  options={options}
                                  optionLabel="label"
                                  optionValue="value"
                                  onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, field.fieldKey, String(e.value || ""))}
                                  className="w-full"
                                  placeholder={field.placeholder || "Select option"}
                                />
                              ) : (
                                <InputText
                                  id={fieldId}
                                  value={field.value || ""}
                                  onChange={(e) => updateFacilityDocumentDraftField(section.sectionKey, field.fieldKey, e.target.value || "")}
                                  className="w-full"
                                  style={{ minWidth: 0 }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div style={appMutedTextStyle}>No editable sections are available for this document yet.</div>
        )}
      </Dialog>

      {/* Show compare assessment summary in HTML format */}
      <Dialog
        header={renderSurfaceHeader("Assessment Comparison", "Compare the latest assessment summary with the previous one.", "pi pi-chart-bar")}
        visible={showCompareAssessmentPopUp}
        style={{ width: '90%' }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => setShowCompareAssessmentPopUp(false)} closeOnEscape={false} footer={
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
            {AI_MESSAGE_FOR_COMPLIANCE}
          </p>
        }>
        <div>
          {
            compareAssessmentSummaryData ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(compareAssessmentSummaryData) }} />
            ) : (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-2">
                  Comparing summaries. This usually takes about 5 minutes. Please keep the browser open and let this request finish before starting another one.
                </div>
              </div>
            )
          }
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Behavior and Intervention Import", "Append behavior and intervention details to existing care plan behaviors only.", "pi pi-sitemap")}
        visible={showBehaviorInterventionImportDialog}
        style={mobile ? { width: '94vw' } : { ...appDialogStyle, width: '88vw', maxWidth: '1600px' }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          if (!processingBehaviorInterventionImport && !uploadingBehaviorInterventionSources) {
            setShowBehaviorInterventionImportDialog(false);
            window.location.reload();
          }
        }}
        closeOnEscape={false}
        footer={
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
            {AI_MESSAGE_FOR_COMPLIANCE}
          </p>
        }
      >
        <div>
          {!processingBehaviorInterventionImport && !behaviorInterventionImportCompletedSuccessfully && (
            <>
              <div
                style={{
                  border: "1px solid #dbe7f3",
                  borderRadius: "14px",
                  background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                  padding: "0.9rem 1rem",
                  marginBottom: "1rem",
                }}
              >
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.25rem" }}>
                  Import into existing behavior rows only
                </div>
                <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                  Use behavior support plans, intervention notes, scanned images, or related source files. The system will extract behavior and intervention content, map it to existing care plan behavior list only, and append the new details without creating new behavior rows.
                </div>
              </div>
              {!canUseSharedResidentBinder ? (
                <Message
                  severity="warn"
                  text="Binder access is not enabled for this shared resident. Binder-based import options are unavailable."
                  className="w-full mb-4"
                />
              ) : null}

              {behaviorInterventionBinderDocuments.length ? (
                <>
                  <div className="flex flex-column gap-3 mb-4">
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Use files already in the resident binder</div>
                    <MultiSelect
                      value={selectedBinderBehaviorDocumentIds}
                      options={behaviorInterventionBinderDocuments}
                      optionLabel="title"
                      optionValue="binderDocumentId"
                      onChange={(e) => setSelectedBinderBehaviorDocumentIds(e.value || [])}
                      placeholder="Choose one or more binder files"
                      className="w-full"
                      display="chip"
                      itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
                      selectedItemTemplate={(option: BinderDocument) => <span>{option?.title || "Binder file"}</span>}
                      emptyMessage="No matching binder files found."
                    />
                    <div className="flex flex-wrap align-items-center gap-2">
                      <Button
                        label="Import Selected Binder Files"
                        severity="warning"
                        outlined
                        rounded
                        disabled={!canUseSharedResidentBinder || !selectedBinderBehaviorDocumentIds.length || uploadingBehaviorInterventionSources || processingBehaviorInterventionImport}
                        onClick={() => { void importBehaviorInterventionFromBinder(); }}
                      />
                      <Button
                        label="Open Binder"
                        severity="secondary"
                        outlined
                        rounded
                        disabled={!canUseSharedResidentBinder}
                        onClick={() => navigate(residentBinderPath)}
                      />
                    </div>
                  </div>
                  <Divider align="center">or upload source files to the binder and import them now</Divider>
                </>
              ) : null}

              <FileUpload
                mode="advanced"
                name="behaviorInterventionFiles"
                multiple
                onSelect={handleBehaviorInterventionFileChange}
                accept={BEHAVIOR_IMPORT_UPLOAD_ACCEPT}
                maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
                headerTemplate={fileHeaderTemplate}
                className="col"
                chooseOptions={chooseOptions}
                cancelOptions={cancelOptions}
                uploadOptions={uploadOptions}
                customUpload
                disabled={!canUseSharedResidentBinder}
                uploadHandler={(event: any) => { void uploadBehaviorInterventionFiles(event?.files || []); }}
                emptyTemplate={<p className="m-0">Upload PDF, DOC, DOCX, or scanned image files here. Each file will be saved into the resident binder before the behavior import runs.</p>}
              />

              {!uploadingBehaviorInterventionSources && !processingBehaviorInterventionImport ? (
                <p>
                  <strong>Note: </strong>
                  <i>
                    Imported content will append into existing behavior rows only. New behaviors that do not clearly map to an existing care plan behavior will be skipped.
                  </i>
                </p>
              ) : null}
            </>
          )}

          {(uploadingBehaviorInterventionSources || processingBehaviorInterventionImport) && (
            <div className="col-12 text-center d-flex justify-content-center align-items-center">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              <div className="block mt-2">
                {uploadingBehaviorInterventionSources && !processingBehaviorInterventionImport
                  ? "Uploading source files to the binder..."
                  : `Processing... ${behaviorInterventionImportProcessingTime} seconds elapsed`}
              </div>
            </div>
          )}

          {behaviorInterventionImportJobDetails && !processingBehaviorInterventionImport ? (
            <div className="mt-4">
              {behaviorInterventionImportCompletedSuccessfully ? (
                <div
                  style={{
                    background: "#d1fae5",
                    border: "1.5px solid #34d399",
                    borderRadius: "10px",
                    padding: "0.9rem 1.1rem",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.7rem",
                    marginBottom: "1rem",
                  }}
                >
                  <i className="pi pi-check-circle" style={{ color: "#059669", fontSize: "1.3rem", marginTop: "0.1rem", flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 700, color: "#065f46", fontSize: "0.95rem", marginBottom: "0.2rem" }}>
                      Pending Review
                    </div>
                    <div style={{ color: "#047857", fontSize: "0.88rem" }}>
                      {Number(behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary?.matchedCount || 0)} behavior row(s) were matched from the selected source files. Review the preview, then update the care plan when ready.
                    </div>
                  </div>
                </div>
              ) : null}

              {behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary ? (
                <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.6rem" }}>Import Summary</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(behaviorInterventionImportJobDetails.behaviorInterventionImportSummary.sourceDocuments || []).map((fileName: string, index: number) => (
                      <span
                        key={`${fileName}-${index}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          borderRadius: "999px",
                          padding: "0.35rem 0.7rem",
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        <i className="pi pi-file" style={{ fontSize: "0.72rem" }} />
                        {fileName}
                      </span>
                    ))}
                  </div>

                  {(() => {
                    const importSummary = normalizeBehaviorInterventionImportSummary(
                      behaviorInterventionImportJobDetails.behaviorInterventionImportSummary,
                    );
                    const reviewMatches = getBehaviorInterventionReviewMatches(importSummary);
                    const behaviorOptionItems = (importSummary?.availableBehaviorOptions || []).map((option: any) => ({
                      label: option?.behaviorLabel || "Behavior",
                      value: option?.behaviorLabel || "",
                    }));

                    if (!reviewMatches.length) {
                      return (
                        <div style={{ ...appMutedTextStyle, marginBottom: "0.75rem" }}>
                          No behavior rows were matched from the selected files.
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: "grid", gap: "0.9rem", marginBottom: "0.9rem" }}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: mobile ? "1fr" : "minmax(240px, 1.1fr) minmax(220px, 1fr) minmax(220px, 1fr)",
                            gap: "0.75rem",
                            padding: mobile ? "0 0.2rem" : "0 0.25rem",
                            color: "#475569",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          <div>Behavior Mapping</div>
                          <div>Behavior Details</div>
                          <div>Interventions Added</div>
                        </div>
                        {reviewMatches.map((item: any, index: number) => (
                          <div
                            key={String(item?.matchId || `${item?.behaviorLabel || "behavior"}-${index}`)}
                            style={{
                              border: "1px solid #dbe7f3",
                              borderRadius: "14px",
                              background: item?.selected === false ? "#f8fafc" : "#ffffff",
                              padding: "0.95rem 1rem",
                            }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: mobile ? "1fr" : "minmax(240px, 1.1fr) minmax(220px, 1fr) minmax(220px, 1fr)",
                                gap: "0.75rem",
                                alignItems: "start",
                              }}
                            >
                              <div>
                                <div className="flex align-items-center gap-2 mb-2">
                                  <Checkbox
                                    inputId={`behavior-import-${item?.matchId || index}`}
                                    checked={item?.selected !== false}
                                    onChange={(event) => updateBehaviorInterventionReviewMatch(String(item?.matchId || ""), (currentMatch) => ({
                                      ...currentMatch,
                                      selected: !!event.checked,
                                    }))}
                                  />
                                  <label
                                    htmlFor={`behavior-import-${item?.matchId || index}`}
                                    style={{ fontWeight: 700, color: "#0f172a", cursor: "pointer" }}
                                  >
                                    Include
                                  </label>
                                </div>
                                <Dropdown
                                  value={item?.behaviorLabel || ""}
                                  options={behaviorOptionItems}
                                  placeholder="Select an existing behavior"
                                  className="w-full"
                                  onChange={(event) => updateBehaviorInterventionReviewMatch(String(item?.matchId || ""), (currentMatch) => ({
                                    ...currentMatch,
                                    behaviorLabel: String(event.value || ""),
                                  }))}
                                />
                                <div className="mt-2" style={appMutedTextStyle}>
                                  Choose from existing behavior rows, even if that row is currently set to No.
                                </div>
                              </div>
                              <div>
                                <div style={{ ...appInnerCardStyle, padding: "0.8rem 0.9rem", whiteSpace: "pre-wrap" }}>
                                  {item?.behaviorDetails || "No new behavior notes were extracted."}
                                </div>
                              </div>
                              <div>
                                <div style={{ ...appInnerCardStyle, padding: "0.8rem 0.9rem", whiteSpace: "pre-wrap" }}>
                                  {item?.interventionDetails || "No intervention details were extracted."}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {(behaviorInterventionImportJobDetails.behaviorInterventionImportSummary.unmapped || []).length ? (
                    <div
                      style={{
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: "10px",
                        padding: "0.8rem 0.9rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.35rem" }}>Could not map automatically</div>
                      <div style={{ ...appMutedTextStyle, color: "#7c2d12" }}>
                        {(behaviorInterventionImportJobDetails.behaviorInterventionImportSummary.unmapped || []).join(", ")}
                      </div>
                    </div>
                  ) : null}

                  {getSelectedBehaviorInterventionReviewMatches(
                    behaviorInterventionImportJobDetails.behaviorInterventionImportSummary,
                  ).length ? (
                    <div className="mt-3" style={{ display: "grid", gap: "0.75rem" }}>
                      <div
                        style={{
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: "10px",
                          padding: "0.8rem 0.9rem",
                          color: "#1e3a8a",
                          fontSize: "0.88rem",
                          lineHeight: 1.55,
                        }}
                      >
                        On <strong>Update Care Plan</strong>, the imported behavior details will be appended into the selected existing behavior row, and if that behavior is currently marked <strong>No</strong>, it will be turned on.
                      </div>
                      <div className="flex justify-content-end">
                        <Button
                          label={
                            behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary?.appliedToCarePlan
                              ? "Care Plan Updated"
                              : "Update Care Plan"
                          }
                          icon={
                            behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary?.appliedToCarePlan
                              ? "pi pi-check-circle"
                              : "pi pi-save"
                          }
                          severity={
                            behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary?.appliedToCarePlan
                              ? "success"
                              : "warning"
                          }
                          rounded
                          disabled={
                            applyingBehaviorInterventionImport
                            || behaviorInterventionImportJobDetails?.behaviorInterventionImportSummary?.appliedToCarePlan === true
                          }
                          loading={applyingBehaviorInterventionImport}
                          onClick={() => { void applyBehaviorInterventionImportToCarePlan(); }}
                        />
                      </div>
                    </div>
                  ) : null}

                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Job Summary", "Review the latest assessment import result and retry any failed sections.", "pi pi-briefcase")}
        visible={showLastJobDetailsDialog}
        style={{ width: '50%' }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => setShowLastJobDetailsDialog(false)} closeOnEscape={false}>
        <div>
          {
            lastJobDetails !== null ? (
              <>
                <div>Last Import Status: {lastJobDetails['jobSubStatus']}</div>
                <ul style={{ listStyleType: 'none', paddingLeft: 0 }} className="text-left">
                  {(lastJobDetails['ncpSections'] || []).map((section) => (
                    <li key={section.carePlanType} className="mb-2">
                      <strong style={{ textTransform: "capitalize" }}>{section.carePlanType}: </strong>
                      <span style={{ color: section.success ? 'green' : 'red' }}>
                        {
                          section.success ? 'Success' : `Failed`
                        }
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="m-2">
                  {showLastJobRetryButton && (
                    <Button label="Retry Failed Items" severity="warning" rounded outlined onClick={() => retryAssessmentJob(lastJobDetails['jobId'])} />
                  )}
                </div>
              </>
            ) : (
              <div>No import details available.</div>
            )
          }
        </div>
      </Dialog>
    </div >
  );
};

export default ResidentNcpPage;


